import React, { useEffect, useRef, useState } from "react";
import ResimUpload from "../ResimUpload";
import { API_BASE } from "../config";

import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";

const LANGS = [
  { code: "tr", label: "TR", name: "Türkçe" },
  { code: "en", label: "EN", name: "English" },
  { code: "de", label: "DE", name: "Deutsch" },
  { code: "ar", label: "AR", name: "العربية" },
  { code: "fa", label: "FA", name: "فارسی" },
  { code: "ru", label: "RU", name: "Русский" },
  { code: "sq", label: "SQ", name: "Shqip" },
  { code: "es", label: "ES", name: "Español" },
  { code: "pt", label: "PT", name: "Português" },
];

const CURRENCIES = ["TRY", "USD", "EUR", "CHF"];
const IMAGE_BASE = "/menu/images";
const QR_THEMES = [
{id:"standard",name:"Standart",icon:"☕"},
{id:"luxury_gold",name:"Luxury Gold",icon:"👑"},
{id:"coffee",name:"Coffee House",icon:"☕"},
{id:"modern_dark",name:"Modern Dark",icon:"🌙"},
{id:"nature",name:"Nature Green",icon:"🌿"},

{id:"pizza",name:"Pizza Italy",icon:"🍕"},
{id:"burger",name:"Burger Red",icon:"🍔"},
{id:"steak",name:"Steak Premium",icon:"🥩"},
{id:"sushi",name:"Sushi Black",icon:"🍣"},
{id:"bakery",name:"Bakery Cream",icon:"🥐"},

{id:"icecream",name:"Ice Cream Pink",icon:"🍦"},
{id:"seafood",name:"Seafood Blue",icon:"🐟"},
{id:"turkish",name:"Turkish Style",icon:"🇹🇷"},
{id:"fastfood",name:"Fast Food",icon:"🍟"},
{id:"dessert",name:"Dessert Sweet",icon:"🍰"},

{id:"beauty",name:"Beauty Rose",icon:"🌸"},
{id:"spa",name:"Luxury Spa",icon:"💆"},
{id:"clinic",name:"Clinic White",icon:"⚕️"},
{id:"gold_black",name:"Gold Black",icon:"🖤"},
{id:"purple",name:"Premium Purple",icon:"💜"},

{id:"corporate",name:"Corporate Blue",icon:"💼"},
{id:"orange",name:"MesYat Orange",icon:"🟠"},
{id:"glass",name:"Glass Effect",icon:"💎"},
{id:"minimal",name:"Minimal Gray",icon:"⚪"},
{id:"business",name:"Dark Business",icon:"🏢"},

{id:"neon",name:"Neon Style",icon:"✨"},
{id:"cyber",name:"Cyber",icon:"🤖"},
{id:"gradient",name:"Gradient",icon:"🌈"},
{id:"instagram",name:"Instagram Style",icon:"📸"},
{id:"marble",name:"Luxury Marble",icon:"🏛️"},

{id:"ocean",name:"Ocean",icon:"🌊"},
{id:"forest",name:"Forest",icon:"🌲"},
{id:"vintage",name:"Vintage",icon:"📻"},
{id:"royal",name:"Royal",icon:"👑"},
{id:"wood",name:"Wood Cafe",icon:"🪵"},

{id:"classic",name:"Classic",icon:"⭐"},
{id:"premium",name:"Premium",icon:"💎"},
{id:"summer",name:"Summer",icon:"☀️"},
{id:"night",name:"Night Mode",icon:"🌃"},
{id:"custom",name:"Custom Brand",icon:"🎨"},
];

const CATEGORY_DICT = {

main_dishes: {
tr:"Ana Yemekler",
en:"Main Dishes",
de:"Hauptgerichte",
ar:"الأطباق الرئيسية",
fa:"غذاهای اصلی",
ru:"Основные блюда",
sq:"Ushqimet Kryesore",
es:"Platos principales",
pt:"Pratos principais"
},

grill:{
tr:"Izgara Çeşitleri",
en:"Grilled Dishes",
de:"Grillgerichte",
ar:"مشويات",
fa:"غذاهای کبابی",
ru:"Гриль",
sq:"Ushqime në skarë",
es:"Parrilladas",
pt:"Grelhados"
},

doner_kebab:{
tr:"Döner & Kebap",
en:"Doner & Kebab",
de:"Döner & Kebab",
ar:"دونر وكباب",
fa:"دونر و کباب",
ru:"Донер и кебаб",
sq:"Doner & Qebap",
es:"Doner y Kebab",
pt:"Doner e Kebab"
},

fish:{
tr:"Balık Çeşitleri",
en:"Fish Dishes",
de:"Fischgerichte",
ar:"أطباق السمك",
fa:"غذاهای ماهی",
ru:"Рыбные блюда",
sq:"Ushqime peshku",
es:"Pescados",
pt:"Peixes"
},

pizza_pasta:{
tr:"Pizza",
en:"Pizza",
de:"Pizza",
ar:"بيتزا",
fa:"پیتزا",
ru:"Пицца",
sq:"Pizza",
es:"Pizza",
pt:"Pizza"
},

pasta:{
tr:"Makarna",
en:"Pasta",
de:"Pasta",
ar:"معكرونة",
fa:"پاستا",
ru:"Паста",
sq:"Makarona",
es:"Pasta",
pt:"Massa"
},

durum:{
tr:"Dürüm",
en:"Wrap",
de:"Dürüm",
ar:"ساندويتش ملفوف",
fa:"رپ",
ru:"Ролл",
sq:"Mbështjellje",
es:"Wrap",
pt:"Wrap"
},

bakery:{
tr:"Unlu Mamüller",
en:"Bakery Products",
de:"Backwaren",
ar:"المخبوزات",
fa:"محصولات نانوایی",
ru:"Выпечка",
sq:"Produkte furre",
es:"Panadería",
pt:"Padaria"
},

chef_special:{
tr:"Şefin Özel Lezzetleri",
en:"Chef Specials",
de:"Chef-Spezialitäten",
ar:"أطباق الشيف الخاصة",
fa:"غذاهای ویژه سرآشپز",
ru:"Фирменные блюда шефа",
sq:"Specialitetet e shefit",
es:"Especialidades del chef",
pt:"Especialidades do chef"
},

fast_food:{
tr:"Fast Food",
en:"Fast Food",
de:"Fast Food",
ar:"وجبات سريعة",
fa:"فست فود",
ru:"Фастфуд",
sq:"Ushqim i shpejtë",
es:"Comida rápida",
pt:"Comida rápida"
},

gluten_free:{
tr:"Glutensiz Ürünler",
en:"Gluten-Free",
de:"Glutenfrei",
ar:"خالٍ من الغلوتين",
fa:"بدون گلوتن",
ru:"Без глютена",
sq:"Pa gluten",
es:"Sin gluten",
pt:"Sem glúten"
},

home_food:{
tr:"Ev Yemekleri",
en:"Home Cooking",
de:"Hausmannskost",
ar:"أكلات منزلية",
fa:"غذاهای خانگی",
ru:"Домашняя еда",
sq:"Ushqime shtëpie",
es:"Comida casera",
pt:"Comida caseira"
},

hookah:{
tr:"Nargile",
en:"Hookah",
de:"Shisha",
ar:"شيشة",
fa:"قلیان",
ru:"Кальян",
sq:"Nargjile",
es:"Cachimba",
pt:"Shisha"
},

mezze:{
tr:"Mezeler",
en:"Mezzes",
de:"Mezze",
ar:"مقبلات",
fa:"مزه‌ها",
ru:"Закуски",
sq:"Meze",
es:"Mezze",
pt:"Mezze"
},

pastry:{
tr:"Poğaça & Açma",
en:"Pastries",
de:"Gebäck",
ar:"معجنات",
fa:"شیرینی و نان",
ru:"Выпечка",
sq:"Pasta furre",
es:"Bollería",
pt:"Pastelaria"
},

seafood:{
tr:"Deniz Ürünleri",
en:"Seafood",
de:"Meeresfrüchte",
ar:"مأكولات بحرية",
fa:"غذاهای دریایی",
ru:"Морепродукты",
sq:"Ushqime deti",
es:"Mariscos",
pt:"Marisco"
},

sides:{
tr:"Ara Sıcaklar",
en:"Side Dishes",
de:"Beilagen",
ar:"أطباق جانبية",
fa:"مخلفات غذا",
ru:"Гарниры",
sq:"Garnitura",
es:"Guarniciones",
pt:"Acompanhamentos"
},

smoothie_healthy:{
tr:"Smoothie & Sağlıklı İçecekler",
en:"Smoothie & Healthy Drinks",
de:"Smoothie & Gesunde Getränke",
ar:"سموذي ومشروبات صحية",
fa:"اسموتی و نوشیدنی سالم",
ru:"Смузи и полезные напитки",
sq:"Smoothie dhe pije të shëndetshme",
es:"Smoothies y bebidas saludables",
pt:"Smoothies e bebidas saudáveis"
},

vegetarian_vegan:{
tr:"Vejetaryen & Vegan",
en:"Vegetarian & Vegan",
de:"Vegetarisch & Vegan",
ar:"نباتي وفيغان",
fa:"گیاهی و وگان",
ru:"Вегетарианское и веганское",
sq:"Vegjetariane & Vegane",
es:"Vegetariano y vegano",
pt:"Vegetariano e vegano"
},

world_cuisine:{
tr:"Dünya Mutfağı",
en:"World Cuisine",
de:"Weltküche",
ar:"مأكولات عالمية",
fa:"غذاهای بین‌المللی",
ru:"Мировая кухня",
sq:"Kuzhina botërore",
es:"Cocina internacional",
pt:"Cozinha mundial"
}, 


salads:{
tr:"Salatalar",
en:"Salads",
de:"Salate",
ar:"سلطات",
fa:"سالادها",
ru:"Салаты",
sq:"Sallata",
es:"Ensaladas",
pt:"Saladas"
},

breakfast:{
tr:"Kahvaltı",
en:"Breakfast",
de:"Frühstück",
ar:"إفطار",
fa:"صبحانه",
ru:"Завтрак",
sq:"Mëngjes",
es:"Desayuno",
pt:"Pequeno-almoço"
},


toast:{
tr:"Tostlar",
en:"Toasts",
de:"Toasts",
ar:"توست",
fa:"تست‌ها",
ru:"Тосты",
sq:"Toste",
es:"Tostadas",
pt:"Tostas"
},

sandwich:{
tr:"Sandviçler",
en:"Sandwiches",
de:"Sandwiches",
ar:"ساندويتشات",
fa:"ساندویچ‌ها",
ru:"Сэндвичи",
sq:"Sanduiçe",
es:"Sándwiches",
pt:"Sanduíches"
},


soups:{
tr:"Çorbalar",
en:"Soups",
de:"Suppen",
ar:"شوربات",
fa:"سوپ‌ها",
ru:"Супы",
sq:"Supa",
es:"Sopas",
pt:"Sopas"
},

kids_menu:{
tr:"Çocuk Menüsü",
en:"Kids Menu",
de:"Kindermenü",
ar:"قائمة الأطفال",
fa:"منوی کودکان",
ru:"Детское меню",
sq:"Menu për fëmijë",
es:"Menú infantil",
pt:"Menu infantil"
},

hot_drinks:{
tr:"Sıcak İçecekler",
en:"Hot Drinks",
de:"Heiße Getränke",
ar:"مشروبات ساخنة",
fa:"نوشیدنی گرم",
ru:"Горячие напитки",
sq:"Pije të ngrohta",
es:"Bebidas calientes",
pt:"Bebidas quentes"
},


coffee:{
tr:"Kahveler",
en:"Coffee",
de:"Kaffee",
ar:"قهوة",
fa:"قهوه",
ru:"Кофе",
sq:"Kafe",
es:"Café",
pt:"Café"
},

cold_drinks:{
tr:"Soğuk İçecekler",
en:"Cold Drinks",
de:"Kalte Getränke",
ar:"مشروبات باردة",
fa:"نوشیدنی سرد",
ru:"Холодные напитки",
sq:"Pije të ftohta",
es:"Bebidas frías",
pt:"Bebidas frias"
},


alcohol:{
tr:"Alkollü İçecekler",
en:"Alcoholic Drinks",
de:"Alkoholische Getränke",
ar:"مشروبات كحولية",
fa:"نوشیدنی‌های الکلی",
ru:"Алкогольные напитки",
sq:"Pije alkoolike",
es:"Bebidas alcohólicas",
pt:"Bebidas alcoólicas"
},

desserts:{
tr:"Tatlılar",
en:"Desserts",
de:"Desserts",
ar:"حلويات",
fa:"دسرها",
ru:"Десерты",
sq:"Ëmbëlsira",
es:"Postres",
pt:"Sobremesas"
}

};
const ALLERGEN_KEYS = [
  "gluten",
  "crustaceans",
  "egg",
  "fish",
  "peanut",
  "soy",
  "milk",
  "nuts",
  "celery",
  "mustard",
  "sesame",
  "sulphites",
  "lupin",
  "molluscs",
];

const ALLERGEN_LABELS = {
  gluten: "Gluten içeren tahıllar",
  crustaceans: "Kabuklular",
  egg: "Yumurta",
  fish: "Balık",
  peanut: "Yer fıstığı",
  soy: "Soya",
  milk: "Süt ve süt ürünleri / Laktoz",
  nuts: "Sert kabuklu yemişler",
  celery: "Kereviz",
  mustard: "Hardal",
  sesame: "Susam",
  sulphites: "Sülfit / Kükürt dioksit",
  lupin: "Acı bakla",
  molluscs: "Yumuşakçalar",
};

const DIETARY_TAGS = [
  { key: "vegan", label: "Vegan" },
  { key: "vegetarian", label: "Vejetaryen" },
  { key: "gluten_free", label: "Glutensiz" },
  { key: "lactose_free", label: "Laktozsuz" },
  { key: "contains_alcohol", label: "Alkol içerir" },
  { key: "no_pork", label: "Domuz türevi içermez" },
];
const makeLangObject = () =>
  LANGS.reduce((acc, l) => {
    acc[l.code] = "";
    return acc;
  }, {});

const makePriceObject = () =>
  LANGS.reduce((acc, l) => {
    acc[l.code] = "";
    return acc;
  }, {});



const makeCurrencyObject = () =>
  LANGS.reduce((acc, l) => {
    acc[l.code] = "TRY";
    return acc;
  }, {});

const makeComplianceInfo = () => ({
  ingredients: makeLangObject(),
  portion: makeLangObject(),
  warning: makeLangObject(),

  // ✅ Manuel veya tahmini kalori
  calories: "",

  // ✅ Kalori butonla tahmin edildiyse true olur
  calories_estimated: false,

  // ✅ Müşteriye ve panele gösterilecek küçük açıklama
  calorie_note: makeLangObject(),

  tags: [],
});

const emptyDraft = {
  title: makeLangObject(),
  description: makeLangObject(),
  price: makePriceObject(),
  currency: makeCurrencyObject(),

  // ✅ 300 ML / 500 ML / 1 LT gibi fiyat seçenekleri
  price_options: [],

  image: "",
  allergens: [],
  compliance_info: makeComplianceInfo(),
  is_trend: false,
};





function safeJsonParse(value, fallback = []) {
  try {
    return JSON.parse(value || "[]");
  } catch {
    return fallback;
  }
}

// ✅ Çoklu fiyat seçeneklerini güvenli okur.
// Eski ürünlerde boş gelir, hiçbir şeyi bozmaz.
function normalizePriceOptions(value) {
  const list = Array.isArray(value) ? value : safeJsonParse(value, []);

  if (!Array.isArray(list)) return [];

  return list.map((opt, index) => ({
    id: opt.id || `opt-${index}`,
    label: String(opt.label || opt.name || opt.title || "").trim(),
    price: opt.price ?? opt.fiyat ?? "",
    currency: opt.currency || opt.para_birimi || "TRY",
  }));
}

// ✅ Mevzuata uyum alanlarını güvenli okur.
// Eski ürünlerde compliance_info yoksa boş obje üretir, hiçbir şeyi bozmaz.

function normalizeComplianceInfo(value) {
  const raw =
    value && typeof value === "object"
      ? value
      : safeJsonParse(value, {});

  const next = makeComplianceInfo();

  next.calories =
    raw?.calories !== undefined && raw?.calories !== null
      ? String(raw.calories)
      : "";

  next.calories_estimated = raw?.calories_estimated === true;

  next.tags = Array.isArray(raw?.tags) ? raw.tags : [];

  LANGS.forEach((l) => {
    next.ingredients[l.code] =
      raw?.ingredients?.[l.code] ||
      raw?.ingredients?.tr ||
      "";

    next.portion[l.code] =
      raw?.portion?.[l.code] ||
      raw?.portion?.tr ||
      "";

    next.warning[l.code] =
      raw?.warning?.[l.code] ||
      raw?.warning?.tr ||
      "";

    next.calorie_note[l.code] =
      raw?.calorie_note?.[l.code] ||
      raw?.calorie_note?.tr ||
      "";
  });

  return next;
}

/* =========================================================
   ✅ TAHMİNİ KALORİ MOTORU
   - Manuel kaloriyi bozmaz.
   - Butona basınca yaklaşık kcal üretir.
   - Porsiyon/gramaj varsa onu kullanır.
   - Yoksa kategori/ürün adına göre güvenli varsayım yapar.
========================================================= */

const CALORIE_ESTIMATE_NOTE = {
  tr: "Bu kalori değeri ürün içeriği, porsiyon, pişirme yöntemi ve yağ oranına göre yaklaşık olarak hazırlanmıştır. Lütfen işletme tarafından kontrol ediniz.",
  en: "This calorie value is estimated based on ingredients, portion size, cooking method and fat ratio. Please verify it by the business.",
  de: "Dieser Kalorienwert ist anhand von Zutaten, Portionsgröße, Zubereitung und Fettanteil geschätzt. Bitte vom Betrieb prüfen.",
  ar: "هذه السعرات الحرارية تقديرية حسب المكونات والحصة وطريقة التحضير ونسبة الدهون. يرجى التحقق منها من قبل المنشأة.",
  fa: "این مقدار کالری بر اساس مواد، اندازه پرس، روش پخت و میزان چربی به‌صورت تقریبی آماده شده است. لطفاً توسط کسب‌وکار بررسی شود.",
  ru: "Это приблизительная калорийность, рассчитанная по составу, порции, способу приготовления и жирности. Пожалуйста, проверьте значение.",
  sq: "Kjo vlerë kalorike është e përafërt sipas përbërësve, porcionit, mënyrës së gatimit dhe yndyrës. Ju lutemi kontrollojeni.",
  es: "Este valor calórico es estimado según ingredientes, porción, método de cocción y cantidad de grasa. Verifíquelo por favor.",
  pt: "Este valor calórico é estimado com base nos ingredientes, porção, método de preparo e teor de gordura. Por favor, verifique.",
};

function normalizeCalorieText(value) {
  return String(value || "")
    .toLocaleLowerCase("tr-TR")
    .replace(/ı/g, "i")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .replace(/â/g, "a")
    .replace(/î/g, "i")
    .replace(/û/g, "u")
    .replace(/[^a-z0-9\s._-]/g, " ")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function calorieHas(source, keywords = []) {
  const cleanSource = ` ${normalizeCalorieText(source)} `;

  return keywords.some((keyword) => {
    const cleanKeyword = normalizeCalorieText(keyword);

    if (!cleanKeyword) return false;

    const pattern = new RegExp(
      `(^|[^a-z0-9])${escapeRegExp(cleanKeyword)}([^a-z0-9]|$)`,
      "i"
    );

    return pattern.test(cleanSource);
  });
}

function roundCalorie(value) {
  const n = Number(value || 0);

  if (!Number.isFinite(n) || n <= 0) return "";

  return String(Math.max(5, Math.round(n / 10) * 10));
}

function extractPortionGram(text, fallbackGram) {
  const source = normalizeCalorieText(text);

  const kg = source.match(/(\d+(?:[.,]\d+)?)\s*(kg|kilogram|kilo)\b/i);
  if (kg) return Number(String(kg[1]).replace(",", ".")) * 1000;

  const gram = source.match(/(\d+(?:[.,]\d+)?)\s*(gr|g|gram)\b/i);
  if (gram) return Number(String(gram[1]).replace(",", "."));

  const ml = source.match(/(\d+(?:[.,]\d+)?)\s*(ml|cc)\b/i);
  if (ml) return Number(String(ml[1]).replace(",", "."));

  const cl = source.match(/(\d+(?:[.,]\d+)?)\s*(cl)\b/i);
  if (cl) return Number(String(cl[1]).replace(",", ".")) * 10;

  const litre = source.match(/(\d+(?:[.,]\d+)?)\s*(lt|litre|liter|l)\b/i);
  if (litre) return Number(String(litre[1]).replace(",", ".")) * 1000;

  return fallbackGram;
}

const CALORIE_RULES = [
  // ✅ Sıcak / soğuk içecekler
  {
    keywords: ["su", "sade soda", "maden suyu"],
    kcalPer100: 0,
    portionGram: 330,
  },
  {
    keywords: ["cay", "turk cayi", "ada cayi", "bitki cayi", "yesil cay", "siyah cay"],
    kcalPer100: 2,
    portionGram: 150,
  },
  {
    keywords: ["espresso", "americano", "filtre kahve", "turk kahvesi", "sade kahve"],
    kcalPer100: 5,
    portionGram: 120,
  },
  {
    keywords: ["latte", "cappuccino", "flat white", "sutlu kahve", "mocha", "macchiato"],
    kcalPer100: 65,
    portionGram: 250,
  },
  {
    keywords: ["sicak cikolata", "salep"],
    kcalPer100: 95,
    portionGram: 250,
  },
  {
    keywords: ["limonata", "meyve suyu", "portakal suyu", "cola", "kola", "gazoz", "ice tea", "buzlu cay"],
    kcalPer100: 45,
    portionGram: 330,
  },
  {
    keywords: ["smoothie", "milkshake", "frozen", "frappe"],
    kcalPer100: 95,
    portionGram: 350,
  },
  {
    keywords: ["atom", "atom icecek", "muzlu atom", "balli atom"],
    kcalPer100: 115,
    portionGram: 400,
  },

  // ✅ Çorbalar
  {
    keywords: ["mercimek corbasi", "ezogelin", "domates corbasi", "yayla corbasi", "tarhana corbasi"],
    kcalPer100: 65,
    portionGram: 250,
  },
  {
    keywords: ["iskembe corbasi", "kelle paca", "tavuk suyu corbasi", "et suyu corbasi"],
    kcalPer100: 90,
    portionGram: 300,
  },

  // ✅ Kahvaltı
  {
    keywords: ["serpme kahvalti", "kahvalti tabagi"],
    kcalPer100: 240,
    portionGram: 500,
  },
  {
    keywords: ["menemen"],
    kcalPer100: 150,
    portionGram: 250,
  },
  {
    keywords: ["omlet", "omelet", "sahanda yumurta"],
    kcalPer100: 180,
    portionGram: 200,
  },
  {
    keywords: ["sucuklu yumurta", "pastirmali yumurta"],
    kcalPer100: 260,
    portionGram: 220,
  },

  // ✅ Izgara, kebap, et ürünleri
  {
    keywords: ["adana kebap", "urfa kebap", "kebap", "sis kebap", "cop sis"],
    kcalPer100: 270,
    portionGram: 280,
  },
  {
    keywords: ["kofte", "izgara kofte", "kasap kofte", "akcaabat kofte", "inegol kofte"],
    kcalPer100: 260,
    portionGram: 250,
  },
  {
    keywords: ["tavuk sis", "tavuk izgara", "tavuk kanat", "kanat", "tavuk pirzola"],
    kcalPer100: 220,
    portionGram: 300,
  },
  {
    keywords: ["pirzola", "kuzu pirzola", "kuzu sis", "kuzu tandir"],
    kcalPer100: 300,
    portionGram: 300,
  },
  {
    keywords: ["antrikot", "bonfile", "lokum", "steak", "biftek"],
    kcalPer100: 270,
    portionGram: 300,
  },
  {
    keywords: ["ciger", "arnavut cigeri", "yaprak ciger"],
    kcalPer100: 230,
    portionGram: 250,
  },

  // ✅ Döner, tantuni, dürüm
  {
    keywords: ["et doner", "yaprak doner", "doner", "soguk doner"],
    kcalPer100: 245,
    portionGram: 250,
  },
  {
    keywords: ["tavuk doner"],
    kcalPer100: 210,
    portionGram: 250,
  },
  {
    keywords: ["doner durum", "et durum", "tavuk durum", "durum"],
    kcalPer100: 250,
    portionGram: 300,
  },
  {
    keywords: ["tantuni", "et tantuni"],
    kcalPer100: 245,
    portionGram: 280,
  },
  {
    keywords: ["tavuk tantuni"],
    kcalPer100: 220,
    portionGram: 280,
  },
  {
    keywords: ["iskender", "iskender kebap"],
    kcalPer100: 280,
    portionGram: 400,
  },
  {
    keywords: ["beyti", "alinazik", "ali nazik"],
    kcalPer100: 270,
    portionGram: 350,
  },

  // ✅ Fast food
  {
    keywords: ["hamburger", "burger", "cheeseburger", "double burger"],
    kcalPer100: 285,
    portionGram: 260,
  },
  {
    keywords: ["tost", "kasarli tost", "sucuklu tost", "karisik tost"],
    kcalPer100: 285,
    portionGram: 200,
  },
  {
    keywords: ["sandvic", "sandwich", "club sandwich", "soguk sandvic"],
    kcalPer100: 240,
    portionGram: 230,
  },
  {
    keywords: ["patates kizartmasi", "kizarmis patates", "french fries"],
    kcalPer100: 310,
    portionGram: 180,
  },
  {
    keywords: ["nugget", "chicken nugget"],
    kcalPer100: 290,
    portionGram: 180,
  },
  {
    keywords: ["sogan halkasi", "onion rings"],
    kcalPer100: 330,
    portionGram: 150,
  },

  // ✅ Pizza, pide, lahmacun
  {
    keywords: ["pizza", "margherita", "karisik pizza", "sucuklu pizza", "vegetarian pizza"],
    kcalPer100: 270,
    portionGram: 320,
  },
  {
    keywords: ["pide", "kusbasili pide", "kiymali pide", "kasarli pide", "karisik pide"],
    kcalPer100: 285,
    portionGram: 300,
  },
  {
    keywords: ["lahmacun"],
    kcalPer100: 240,
    portionGram: 180,
  },

  // ✅ Makarna, pilav, mantı
  {
    keywords: ["makarna", "spaghetti", "penne", "fettuccine", "tagliatelle", "noodle"],
    kcalPer100: 180,
    portionGram: 300,
  },
  {
    keywords: ["kremali makarna", "alfredo", "carbonara"],
    kcalPer100: 235,
    portionGram: 320,
  },
  {
    keywords: ["pilav", "pirinc pilavi", "bulgur pilavi"],
    kcalPer100: 170,
    portionGram: 250,
  },
  {
    keywords: ["manti", "kayseri mantisi"],
    kcalPer100: 240,
    portionGram: 300,
  },

  // ✅ Ev yemekleri
  {
    keywords: ["kuru fasulye", "nohut", "barbunya"],
    kcalPer100: 150,
    portionGram: 280,
  },
  {
    keywords: ["tas kebabi", "et sote", "tavuk sote", "guvec"],
    kcalPer100: 190,
    portionGram: 300,
  },
  {
    keywords: ["dolma", "sarma", "yaprak sarma"],
    kcalPer100: 170,
    portionGram: 250,
  },
  {
    keywords: ["karnıyarik", "karniyarik", "imam bayildi", "musakka"],
    kcalPer100: 180,
    portionGram: 300,
  },

  // ✅ Salata, meze, sağlıklı ürünler
  {
    keywords: ["salata", "yesil salata", "coban salata", "mevsim salata"],
    kcalPer100: 75,
    portionGram: 250,
  },
  {
    keywords: ["sezar salata", "caesar salad"],
    kcalPer100: 170,
    portionGram: 300,
  },
  {
    keywords: ["ton balikli salata", "tavuklu salata"],
    kcalPer100: 145,
    portionGram: 300,
  },
  {
    keywords: ["humus", "haydari", "acili ezme", "atom meze", "meze"],
    kcalPer100: 180,
    portionGram: 150,
  },
  {
    keywords: ["cig kofte", "etsiz cig kofte"],
    kcalPer100: 190,
    portionGram: 200,
  },

  // ✅ Deniz ürünleri
  {
    keywords: ["balik", "levrek", "cipura", "somon", "hamsi", "alabalik"],
    kcalPer100: 180,
    portionGram: 300,
  },
  {
    keywords: ["karides", "kalamar", "midye", "deniz urunu"],
    kcalPer100: 170,
    portionGram: 220,
  },

  // ✅ Unlu mamul
  {
    keywords: ["pogaca", "acma", "simit"],
    kcalPer100: 330,
    portionGram: 100,
  },
  {
    keywords: ["borek", "su boregi", "kol boregi", "sigara boregi"],
    kcalPer100: 320,
    portionGram: 180,
  },
  {
    keywords: ["kruvasan", "croissant"],
    kcalPer100: 400,
    portionGram: 90,
  },

  // ✅ Tatlılar
  {
    keywords: ["baklava", "soguk baklava", "sobiyet"],
    kcalPer100: 430,
    portionGram: 120,
  },
  {
    keywords: ["kunefe"],
    kcalPer100: 360,
    portionGram: 180,
  },
  {
    keywords: ["sutlac", "kazandibi", "muhallebi", "tavuk gogsu"],
    kcalPer100: 160,
    portionGram: 180,
  },
  {
    keywords: ["cheesecake", "tiramisu", "magnolia", "profiterol", "pasta", "yas pasta"],
    kcalPer100: 330,
    portionGram: 160,
  },
  {
    keywords: ["dondurma", "ice cream"],
    kcalPer100: 210,
    portionGram: 120,
  },
  {
    keywords: ["waffle"],
    kcalPer100: 300,
    portionGram: 300,
  },

  // ✅ Alkollü içecekler
  {
    keywords: ["bira", "beer"],
    kcalPer100: 43,
    portionGram: 500,
  },
  {
    keywords: ["sarap", "wine"],
    kcalPer100: 85,
    portionGram: 150,
  },
  {
    keywords: ["raki", "viski", "vodka", "gin"],
    kcalPer100: 230,
    portionGram: 50,
  },
];

function getDefaultPortionGram(categoryKey, text) {
  const source = normalizeCalorieText(`${categoryKey} ${text}`);

  const matched = CALORIE_RULES.find((rule) =>
    calorieHas(source, rule.keywords)
  );

  if (matched?.portionGram) return matched.portionGram;

  if (calorieHas(source, ["hot drinks", "sicak icecek", "coffee", "kahve"])) return 250;
  if (calorieHas(source, ["cold drinks", "soguk icecek", "icecek"])) return 330;
  if (calorieHas(source, ["soups", "corba"])) return 250;
  if (calorieHas(source, ["desserts", "tatli"])) return 150;
  if (calorieHas(source, ["pizza"])) return 320;
  if (calorieHas(source, ["pasta", "makarna"])) return 300;
  if (calorieHas(source, ["burger"])) return 260;
  if (calorieHas(source, ["toast", "tost"])) return 200;
  if (calorieHas(source, ["sandwich", "sandvic"])) return 230;
  if (calorieHas(source, ["salads", "salata"])) return 250;
  if (calorieHas(source, ["fish", "balik"])) return 300;
  if (calorieHas(source, ["grill", "izgara", "doner", "kebap"])) return 280;

  return 250;
}

function estimateKcalPer100(categoryKey, text) {
  const source = normalizeCalorieText(`${categoryKey} ${text}`);

  const matched = CALORIE_RULES.find((rule) =>
    calorieHas(source, rule.keywords)
  );

  if (matched) return matched.kcalPer100;

  if (calorieHas(source, ["corba"])) return 65;
  if (calorieHas(source, ["salata"])) return 90;
  if (calorieHas(source, ["tatli", "dessert"])) return 320;
  if (calorieHas(source, ["unlu mamul", "bakery", "pastry"])) return 320;
  if (calorieHas(source, ["pizza"])) return 270;
  if (calorieHas(source, ["makarna"])) return 180;
  if (calorieHas(source, ["burger"])) return 285;
  if (calorieHas(source, ["tost"])) return 285;
  if (calorieHas(source, ["sandvic", "wrap", "durum"])) return 245;
  if (calorieHas(source, ["balik", "fish"])) return 180;
  if (calorieHas(source, ["deniz urunu", "seafood"])) return 170;
  if (calorieHas(source, ["izgara", "grill", "doner", "kebap", "kofte", "et"])) return 255;
  if (calorieHas(source, ["kizartma", "fried"])) return 310;
  if (calorieHas(source, ["kahvalti"])) return 230;
  if (calorieHas(source, ["icecek", "drink"])) return 45;

  return 220;
}

function adjustKcalPer100(base, text) {
  const source = normalizeCalorieText(text);
  let next = Number(base || 220);

  // ✅ Kaloriyi artıran içerikler
  if (calorieHas(source, ["kasar", "peynir", "cheddar", "mozzarella", "cheese"])) next += 35;
  if (calorieHas(source, ["krema", "cream", "kremali"])) next += 45;
  if (calorieHas(source, ["mayonez", "mayonnaise"])) next += 60;
  if (calorieHas(source, ["tereyag", "butter"])) next += 45;
  if (calorieHas(source, ["zeytinyag", "aycicek yagi", "yag", "oil"])) next += 35;
  if (calorieHas(source, ["kizartma", "kizartilmis", "fried"])) next += 55;
  if (calorieHas(source, ["seker", "sugar", "surup", "syrup", "bal", "honey"])) next += 30;
  if (calorieHas(source, ["cikolata", "chocolate", "nutella"])) next += 65;
  if (calorieHas(source, ["sucuk", "sosis", "pastirma", "pepperoni"])) next += 45;
  if (calorieHas(source, ["kaymak"])) next += 60;
  if (calorieHas(source, ["tahin"])) next += 70;
  if (calorieHas(source, ["fistik", "findik", "ceviz", "badem"])) next += 55;

  // ✅ Kaloriyi düşüren ifadeler
  if (calorieHas(source, ["light", "fit", "diyet", "sekersiz", "sugar free"])) next -= 35;
  if (calorieHas(source, ["az yagli", "low fat"])) next -= 25;
  if (calorieHas(source, ["haslama", "bugulama", "grilled", "izgara"])) next -= 10;
  if (calorieHas(source, ["vegan", "vejetaryen", "vegetarian"])) next -= 10;

  return Math.max(5, next);
}

function buildCalorieEstimate(product, categoryKey, lang = "tr") {
  const compliance = normalizeComplianceInfo(product?.compliance_info);

  const title = getLangValue(product?.title, lang);
  const description = getLangValue(product?.description, lang);
  const ingredients = getLangValue(compliance.ingredients, lang);
  const portion = getLangValue(compliance.portion, lang);

  const fullText = [
    categoryKey,
    title,
    description,
    ingredients,
    portion,
  ].join(" ");

  const fallbackGram = getDefaultPortionGram(categoryKey, fullText);
  const portionGram = extractPortionGram(fullText, fallbackGram);
  const baseKcal = estimateKcalPer100(categoryKey, fullText);
  const adjustedKcal = adjustKcalPer100(baseKcal, fullText);

  const calories = roundCalorie((adjustedKcal * portionGram) / 100);

  return {
    calories,
    note:
      CALORIE_ESTIMATE_NOTE[lang] ||
      CALORIE_ESTIMATE_NOTE.tr,
  };
}


function getLangValue(obj, lang) {


  return obj?.[lang] || obj?.tr || obj?.en || obj?.de || "";
}

function getCategoryTitle(cat, lang) {
  const customTitle = getLangValue(cat.customTitle, lang);

  if (customTitle && String(customTitle).trim() !== "") {
    return customTitle;
  }

  const readyTitle = CATEGORY_DICT[cat.key];

  if (readyTitle) {
    return readyTitle?.[lang] || readyTitle?.tr || cat.key;
  }

  return cat.key;
}


function getDbImage(row) {
  return (
    row?.image ||
    row?.resim_url ||
    row?.gorsel ||
    row?.category_image ||
    row?.kategori_resmi ||
    row?.kategori_gorsel ||
    row?.cover_image ||
    row?.kapak_resmi ||
    ""
  );
}

function resolvePanelCategoryImage(cat) {
  const uploadedImage =
    cat?.categoryImage ||
    cat?.image ||
    cat?.resim_url ||
    cat?.gorsel ||
    cat?.category_image ||
    "";

  if (String(uploadedImage).trim() !== "") {
    return uploadedImage;
  }

  if (cat?.key) {
    return `${IMAGE_BASE}/${cat.key}.jpg`;
  }

  return `${IMAGE_BASE}/default.jpg`;
}


const NOTICE_TEXT = {
  tr: {
    saved: "Kaydedildi.",
    error: "İşlem tamamlanamadı.",
    connectionError: "Bağlantı hatası.",
    titleRequired: "En az bir dilde ürün adı yazmalısınız.",
       themeRequired: "En az 1 tema seçili kalmalıdır.",
    categoryRequired: "En az bir dilde kategori adı yazmalısınız.",
  },
  en: {
    saved: "Saved.",
    error: "Operation could not be completed.",
    connectionError: "Connection error.",
    titleRequired: "You must enter a product name in at least one language.",
    themeRequired: "At least 1 theme must remain selected.",
  },
  de: {
    saved: "Gespeichert.",
    error: "Der Vorgang konnte nicht abgeschlossen werden.",
    connectionError: "Verbindungsfehler.",
    titleRequired: "Sie müssen in mindestens einer Sprache einen Produktnamen eingeben.",
    themeRequired: "Mindestens 1 Thema muss ausgewählt bleiben.",
  },
  ar: {
    saved: "تم الحفظ.",
    error: "تعذر إكمال العملية.",
    connectionError: "خطأ في الاتصال.",
    titleRequired: "يجب إدخال اسم المنتج بلغة واحدة على الأقل.",
    themeRequired: "يجب أن يبقى قالب واحد على الأقل محددًا.",
  },
  fa: {
    saved: "ذخیره شد.",
    error: "عملیات انجام نشد.",
    connectionError: "خطای اتصال.",
    titleRequired: "باید نام محصول را حداقل در یک زبان وارد کنید.",
    themeRequired: "حداقل یک قالب باید انتخاب شده باشد.",
  },
  ru: {
    saved: "Сохранено.",
    error: "Не удалось выполнить операцию.",
    connectionError: "Ошибка подключения.",
    titleRequired: "Введите название товара хотя бы на одном языке.",
    themeRequired: "Должна остаться выбрана минимум 1 тема.",
  },
  sq: {
    saved: "U ruajt.",
    error: "Veprimi nuk u krye.",
    connectionError: "Gabim lidhjeje.",
    titleRequired: "Duhet të shkruani emrin e produktit të paktën në një gjuhë.",
    themeRequired: "Duhet të mbetet e zgjedhur të paktën 1 temë.",
  },
  es: {
    saved: "Guardado.",
    error: "No se pudo completar la operación.",
    connectionError: "Error de conexión.",
    titleRequired: "Debe escribir el nombre del producto en al menos un idioma.",
    themeRequired: "Debe quedar seleccionado al menos 1 tema.",
  },
  pt: {
    saved: "Guardado.",
    error: "A operação não pôde ser concluída.",
    connectionError: "Erro de ligação.",
    titleRequired: "Deve inserir o nome do produto em pelo menos um idioma.",
    themeRequired: "Pelo menos 1 tema deve permanecer selecionado.",
  },
};

function getNoticeText(lang, key) {
  return NOTICE_TEXT?.[lang]?.[key] || NOTICE_TEXT.tr[key] || key;
}

function LangMiniButtons({ langs, activeLang, onSelect, showName = false }) {
  return (
    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-9 gap-1.5">
      {langs.map((l) => {
        const active = activeLang === l.code;

        return (
          <button
            key={l.code}
            type="button"
            onClick={() => onSelect(l.code)}
            className={`relative overflow-hidden h-9 rounded-xl border text-[11px] font-black transition-all duration-200 active:scale-95 hover:-translate-y-0.5 ${
              active
                ? "bg-gradient-to-br from-orange-500 to-orange-700 text-white border-orange-600 shadow-md"
                : "bg-white text-gray-700 border-gray-200 hover:border-orange-300 hover:bg-orange-50"
            }`}
          >
            <span className="relative z-10">
              {l.label}
              {showName ? ` · ${l.name}` : ""}
            </span>

            {active && (
              <span className="absolute right-1 top-1 w-2 h-2 rounded-full bg-white/90" />
            )}
          </button>
        );
      })}
    </div>
  );
}


function SortableCategoryBox({ cat, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `cat-${cat.id}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.65 : 1,
    zIndex: isDragging ? 50 : "auto",
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({
        dragHandleProps: {
          ...attributes,
          ...listeners,
        },
      })}
    </div>
  );
}

function SortableProductBox({ item, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `item-${item.id}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.65 : 1,
    zIndex: isDragging ? 50 : "auto",
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({
        dragHandleProps: {
          ...attributes,
          ...listeners,
        },
      })}
    </div>
  );
}


export default function MenuYonetimiPage() {
  const [uiLang] = useState(localStorage.getItem("app_lang") || "tr");
  const [menuLangs, setMenuLangs] = useState(["tr"]);
  const [menuCategories, setMenuCategories] = useState([]);
  const [openCategoryId, setOpenCategoryId] = useState(null);
  const [formLang, setFormLang] = useState("tr");
  const [loading, setLoading] = useState(true);


  const [saving, setSaving] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [appNotice, setAppNotice] = useState(null);
  const [uploadResetKeys, setUploadResetKeys] = useState({});
const [confirmBox, setConfirmBox] = useState(null);
const [customCategoryDraft, setCustomCategoryDraft] = useState(makeLangObject());

// ✅ Ürün ekleme ekranını tek tıkla açıp oraya kaydırmak için
const [activeAddProductCategoryId, setActiveAddProductCategoryId] = useState(null);
const addProductFormRefs = useRef({});
const productNameInputRefs = useRef({});

  const showAppNotice = (key, type = "success") => {
    setAppNotice({
      text: getNoticeText(uiLang, key),
      type,
    });

    setTimeout(() => {
      setAppNotice(null);
    }, 2500);
  };

const openAddProductForm = (categoryId) => {
  setOpenCategoryId(categoryId);
  setActiveAddProductCategoryId(categoryId);

  setTimeout(() => {
    const formEl = addProductFormRefs.current[categoryId];

    if (formEl) {
      formEl.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }

    setTimeout(() => {
      productNameInputRefs.current[categoryId]?.focus?.();
    }, 180);
  }, 120);
};

const closeAddProductForm = (categoryId) => {
  setActiveAddProductCategoryId((prev) =>
    prev === categoryId ? null : prev
  );
};

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 120,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

const [menuOptions,setMenuOptions]=useState({
garson_cagir_active:true,
siparis_active:true,

theme_default:"modern_dark",

theme_options:[
  "modern_dark",
  "gold_black",
  "business",
  "luxury_gold",
  "coffee",
  "standard"
]
});



const [googleReview, setGoogleReview] = useState({
  google_review_url: "",
  google_review_active: false,
  google_review_title_json: {
    tr: "Bizi Google’da Değerlendirin",
    en: "Review us on Google",
    de: "Bewerten Sie uns auf Google",
    ar: "قيّمنا على Google",
    fa: "ما را در Google ارزیابی کنید",
    ru: "Оцените нас в Google",
    sq: "Na vlerësoni në Google",
    es: "Valóranos en Google",
    pt: "Avalie-nos no Google",
  },
  google_review_desc_json: {
    tr: "Deneyiminizi paylaşarak bizi destekleyebilirsiniz.",
    en: "Share your experience and support us.",
    de: "Teilen Sie Ihre Erfahrung und unterstützen Sie uns.",
    ar: "شارك تجربتك وادعمنا.",
    fa: "تجربه خود را به اشتراک بگذارید.",
    ru: "Поделитесь своим опытом.",
    sq: "Ndani përvojën tuaj me ne.",
    es: "Comparte tu experiencia con nosotros.",
    pt: "Partilhe a sua experiência connosco.",
  },
});

  const token = localStorage.getItem("token");

  const loadSettings = async () => {
    try {
      const res = await fetch(`${API_BASE}/kurum-ayarlar`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) return;

      const json = await res.json();
      const d = json?.data || {};
setMenuOptions({
garson_cagir_active:
d.garson_cagir_active !== false,

siparis_active:
d.siparis_active !== false,

theme_default:
d.theme_default || "modern_dark",

theme_options:
Array.isArray(d.theme_options) && d.theme_options.length
? d.theme_options
:[
"modern_dark",
"gold_black",
"business",
"luxury_gold",
"coffee",
"standard"
]
});
setGoogleReview({
  google_review_url: d.google_review_url || "",
  google_review_active: d.google_review_active === true,

  google_review_title_json:
    Object.keys(d.google_review_title_json || {}).length
      ? d.google_review_title_json
      : googleReview.google_review_title_json,

  google_review_desc_json:
    Object.keys(d.google_review_desc_json || {}).length
      ? d.google_review_desc_json
      : googleReview.google_review_desc_json,
});


      if (Array.isArray(d.menu_langs) && d.menu_langs.length) {
        setMenuLangs(d.menu_langs);
        setFormLang(d.menu_langs[0]);
      } else {
        setMenuLangs(["tr"]);
        setFormLang("tr");
      }
    } catch (e) {
      console.error("Menü dil ayarı okunamadı:", e);
    }
  };

  const loadMenuFromBackend = async () => {
    if (!token) return;
    setLoading(true);

    try {
      const secRes = await fetch(`${API_BASE}/extra-sections`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const secJson = await secRes.json();

      const catRes = await fetch(`${API_BASE}/extra-categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const catJson = await catRes.json();

      const itemRes = await fetch(`${API_BASE}/extra-items`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const itemJson = await itemRes.json();

           const result = [];

      const sortedSections = [...(secJson.data || [])].sort((a, b) => {
        const siraA = a.sira ?? 9999;
        const siraB = b.sira ?? 9999;
        return siraA - siraB || a.id - b.id;
      });

      const sortedCategories = [...(catJson.data || [])];
      const sortedItems = [...(itemJson.data || [])];

      for (const sec of sortedSections) {
        const cats = sortedCategories
          .filter((c) => c.section_id === sec.id)
          .sort((a, b) => {
            const siraA = a.sira ?? 9999;
            const siraB = b.sira ?? 9999;
            return siraA - siraB || a.id - b.id;
          });

        for (const cat of cats) {
          const items = sortedItems
            .filter((i) => i.category_id === cat.id)
            .sort((a, b) => {
              const siraA = a.sira ?? 9999;
              const siraB = b.sira ?? 9999;
              return siraA - siraB || a.id - b.id;
            })
            .map((i) => {



              const title = makeLangObject();
              const description = makeLangObject();
              const price = makePriceObject();
              const currency = makeCurrencyObject();

              LANGS.forEach((l) => {
                title[l.code] = i[`title_${l.code}`] || "";
                description[l.code] = i[`description_${l.code}`] || "";
                price[l.code] = i[`price_${l.code}`] || i.price || "";
                currency[l.code] = i[`currency_${l.code}`] || i.currency || "TRY";
              });

    


return {
  id: i.id,
  title,
  description,
  price,
  currency,

  // ✅ Çoklu fiyat / boy seçenekleri
  price_options: normalizePriceOptions(i.price_options),

  image: i.image || "",
  allergens: Array.isArray(i.allergens)
    ? i.allergens
    : safeJsonParse(i.allergens, []),

  // ✅ Yeni mevzuat alanları: içerik, kalori, porsiyon, uyarı, etiketler
  compliance_info: normalizeComplianceInfo(i.compliance_info),

  is_trend: i.is_trend === true,
};


            });

                    const sectionTitle = makeLangObject();

          LANGS.forEach((l) => {
            sectionTitle[l.code] = sec[`title_${l.code}`] || "";
          });

            result.push({
  id: sec.id,
  key: sec.category_key,

  // ✅ Hazır kategori bile olsa DB'deki başlığı alır.
  // Böylece "Dürüm & Kebap" yerine panelden "Dürüm" yazılabilir.
  customTitle: sectionTitle,

   categoryImage:
    getDbImage(cat) ||
    getDbImage(sec) ||
    `${IMAGE_BASE}/${sec.category_key || "default"}.jpg`,

  items,
  draft: JSON.parse(JSON.stringify(emptyDraft)),
});
        }
      }

      setMenuCategories(result);
    } catch (e) {
      console.error("Menü yüklenemedi:", e);
      showAppNotice("error", "error");

    } finally {
      setLoading(false);
    }
  };



const saveMenuToBackend = async (overrideCategories = null) => {
  if (!token || saving) return;

  let categoriesToSave = Array.isArray(overrideCategories)
    ? overrideCategories
    : menuCategories;

  // ✅ Ürün düzenleme penceresi açıksa,
  // oradaki 200 ML / 300 ML seçeneklerini de kayda dahil eder.
  if (!Array.isArray(overrideCategories) && editingProduct) {
    categoriesToSave = menuCategories.map((cat, cIndex) => {
      if (cIndex !== editingProduct.cIndex) return cat;

      return {
        ...cat,
        items: cat.items.map((item, iIndex) =>
          iIndex === editingProduct.iIndex
            ? JSON.parse(JSON.stringify(editingProduct.data))
            : item
        ),
      };
    });
  }

  setSaving(true);

  const activeMenuLangs = menuLangs.length ? menuLangs : ["tr"];

  const hasTemporaryRecords = categoriesToSave.some(


    (cat) =>
      String(cat.id).length > 10 ||
      cat.items.some((item) => String(item.id).length > 10)
  );

  const authHeaders = {
    Authorization: `Bearer ${token}`,
  };

  const jsonHeaders = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  const requestJson = async (url, options = {}) => {
    const res = await fetch(url, options);

    if (!res.ok) {
      const errText = await res.text();
      throw new Error(errText || "İstek başarısız.");
    }

    try {
      return await res.json();
    } catch {
      return {};
    }
  };

  try {
    // ✅ 1) Menü dillerini kaydet
    const langRes = await fetch(`${API_BASE}/kurum-ayarlar/menu-langs`, {
      method: "PUT",
      headers: jsonHeaders,
      body: JSON.stringify({
        menu_langs: activeMenuLangs,
      }),
    });

    if (!langRes.ok) {
      const errText = await langRes.text();
      console.error("Menü dilleri kaydedilemedi:", errText);
      showAppNotice("error", "error");
      return;
    }

    localStorage.setItem("menu_langs", JSON.stringify(activeMenuLangs));

    // ✅ 2) Section ve category listesini sadece 1 kere çek
    const [secsJson, catsJson] = await Promise.all([
      fetch(`${API_BASE}/extra-sections`, {
        headers: authHeaders,
      }).then((r) => r.json()),

      fetch(`${API_BASE}/extra-categories`, {
        headers: authHeaders,
      }).then((r) => r.json()),
    ]);

    const secsList = secsJson.data || [];
    const catsList = catsJson.data || [];

    // ✅ 3) Kategori yapısını kaydet
for (let sira = 0; sira < categoriesToSave.length; sira++) {
  const cat = categoriesToSave[sira];

      const hasCustomCategoryTitle = LANGS.some((l) =>
        cat.customTitle?.[l.code]?.trim()
      );

      const label =
        hasCustomCategoryTitle
          ? cat.customTitle
          : CATEGORY_DICT[cat.key] || {};

      let existingSec = secsList.find((s) => s.category_key === cat.key);

      const sectionImage =
        cat.categoryImage ||
        getDbImage(existingSec) ||
        `${IMAGE_BASE}/${cat.key}.jpg`;

      const secPayload = {
        category_key: cat.key,
        aktif: true,
        sira,
        image: sectionImage,
      };

      LANGS.forEach((l) => {
        secPayload[`title_${l.code}`] =
          activeMenuLangs.includes(l.code)
            ? getLangValue(label, l.code) || cat.key
            : "";
      });



      let secData;

      if (existingSec) {
        await requestJson(`${API_BASE}/extra-sections/${existingSec.id}`, {
          method: "PUT",
          headers: jsonHeaders,
          body: JSON.stringify(secPayload),
        });

        secData = {
          ...existingSec,
          ...secPayload,
        };
      } else {
        const createdSec = await requestJson(`${API_BASE}/extra-sections`, {
          method: "POST",
          headers: jsonHeaders,
          body: JSON.stringify(secPayload),
        });

        secData = createdSec.data || createdSec;
        secsList.push(secData);
      }

      let existingCategory = catsList.find(
        (c) => Number(c.section_id) === Number(secData.id)
      );

      const categoryImage =
        cat.categoryImage ||
        getDbImage(existingCategory) ||
        getDbImage(secData) ||
        `${IMAGE_BASE}/${cat.key}.jpg`;

      const catPayload = {
        section_id: secData.id,
        aktif: true,
        sira: 0,
        image: categoryImage,
      };

      LANGS.forEach((l) => {
        catPayload[`title_${l.code}`] =
          activeMenuLangs.includes(l.code)
            ? getLangValue(label, l.code) || cat.key
            : "";
      });

      let categoryData;

      if (existingCategory) {
        await requestJson(`${API_BASE}/extra-categories/${existingCategory.id}`, {
          method: "PUT",
          headers: jsonHeaders,
          body: JSON.stringify(catPayload),
        });

        categoryData = {
          ...existingCategory,
          ...catPayload,
        };
      } else {
        const createdCategory = await requestJson(`${API_BASE}/extra-categories`, {
          method: "POST",
          headers: jsonHeaders,
          body: JSON.stringify(catPayload),
        });

        categoryData = createdCategory.data || createdCategory;
        catsList.push(categoryData);
      }

      // ✅ 4) Ürünleri aynı kategori içinde paralel kaydet
      const itemRequests = cat.items.map((item, i) => {
        const firstActiveLang =
          activeMenuLangs.find((lng) => item.title?.[lng]?.trim()) || "tr";

        const cleanPriceOptions = normalizePriceOptions(item.price_options);

        console.log("🧪 PANEL KAYIT ITEM:", {
          id: item.id,
          title_tr: item.title?.tr,
          raw_price_options: item.price_options,
          clean_price_options: cleanPriceOptions,
        });

const itemPayload = {
  category_id: categoryData.id,
  image: item.image || "",
  allergens: item.allergens || [],
  compliance_info: normalizeComplianceInfo(item.compliance_info),
  is_trend: item.is_trend === true,
  aktif: true,
  sira: i,

  // ✅ Eski ürünleri bozmaz. Boşsa [] gider.
  price_options: cleanPriceOptions,
};
        LANGS.forEach((l) => {
          itemPayload[`title_${l.code}`] =
            activeMenuLangs.includes(l.code)
              ? item.title?.[l.code] || ""
              : "";

          itemPayload[`description_${l.code}`] =
            activeMenuLangs.includes(l.code)
              ? item.description?.[l.code] || ""
              : "";

          itemPayload[`price_${l.code}`] =
            activeMenuLangs.includes(l.code)
              ? item.price?.[l.code] || null
              : null;

          itemPayload[`currency_${l.code}`] =
            activeMenuLangs.includes(l.code)
              ? item.currency?.[l.code] || "TRY"
              : "TRY";
        });

        itemPayload.title_tr =
          itemPayload.title_tr ||
          item.title?.[firstActiveLang] ||
          item.title?.tr ||
          "";

        itemPayload.description_tr =
          itemPayload.description_tr ||
          item.description?.[firstActiveLang] ||
          item.description?.tr ||
          "";

        itemPayload.price =
          item.price?.tr ||
          item.price?.[firstActiveLang] ||
          0;

        itemPayload.currency =
          item.currency?.tr ||
          item.currency?.[firstActiveLang] ||
          "TRY";

        if (String(item.id).length > 10) {
          return requestJson(`${API_BASE}/extra-items`, {
            method: "POST",
            headers: jsonHeaders,
            body: JSON.stringify(itemPayload),
          });
        }

        return requestJson(`${API_BASE}/extra-items/${item.id}`, {
          method: "PUT",
          headers: jsonHeaders,
          body: JSON.stringify(itemPayload),
        });
      });

      await Promise.all(itemRequests);
    }

    showAppNotice("saved");

    // ✅ Yeni kayıt varsa ID almak için menüyü bir kere yenile.
    // Sadece düzenleme/sıralama yaptıysan tekrar yükleme yapmaz, çok hızlanır.
    if (hasTemporaryRecords) {
      await loadMenuFromBackend();
    }
  } catch (e) {
    console.error("Menü kaydedilemedi:", e);
    showAppNotice("error", "error");
  } finally {
    setSaving(false);
  }
};





const saveMenuOptions = async () => {
  await fetch(`${API_BASE}/kurum-ayarlar/menu-options`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(menuOptions),
  });

   showAppNotice("saved");
};

const saveGoogleReview = async () => {
  try {
    const res = await fetch(`${API_BASE}/kurum-ayarlar/google-review`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
body: JSON.stringify({
  google_review_url: googleReview.google_review_url || "",
  google_review_active:
    googleReview.google_review_active === true &&
    String(googleReview.google_review_url || "").trim() !== "",
  google_review_title_json: googleReview.google_review_title_json || {},
  google_review_desc_json: googleReview.google_review_desc_json || {},
}),
    });

    const data = await res.json();

    if (!res.ok) {
      console.error("Google kayıt hata:", data);
      showAppNotice("error", "error");
      return;
    }

    showAppNotice("saved");

  } catch (err) {
    console.error("Google bağlantı hata:", err);
    showAppNotice("connectionError", "error");
  }
};
  useEffect(() => {
    loadSettings();
    loadMenuFromBackend();
  }, []);

  const updateDraft = (cIndex, group, lang, value) => {
    const copy = [...menuCategories];
    copy[cIndex].draft[group][lang] = value;
    setMenuCategories(copy);
  };

  const updateDraftSingle = (cIndex, field, value) => {
    const copy = [...menuCategories];
    copy[cIndex].draft[field] = value;
    setMenuCategories(copy);
  };

   const addCategory = async (key) => {
  if (!key) return;

  const existingCategory = menuCategories.find((c) => c.key === key);

  if (existingCategory) {
    openAddProductForm(existingCategory.id);
    return;
  }

  const newId = Date.now();
  let dbCategoryImage = "";

  // ✅ Hazır kategori seçilince kategori görselini DB’den bulur.
  // Böylece otomatik seçilen kategoriler boş / amatör görünmez.
  try {
    const [secRes, catRes] = await Promise.all([
      fetch(`${API_BASE}/extra-sections`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      fetch(`${API_BASE}/extra-categories`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    ]);

    const secJson = await secRes.json();
    const catJson = await catRes.json();

    const secsList = secJson.data || [];
    const catsList = catJson.data || [];

    const existingSec = secsList.find((s) => s.category_key === key);

    const existingCat = existingSec
      ? catsList.find((c) => Number(c.section_id) === Number(existingSec.id))
      : null;

    dbCategoryImage =
      getDbImage(existingCat) ||
      getDbImage(existingSec) ||
      "";
  } catch (err) {
    console.error("Hazır kategori görseli DB’den alınamadı:", err);
  }

  const newCategory = {
    id: newId,
    key,
    customTitle: JSON.parse(
      JSON.stringify(CATEGORY_DICT[key] || makeLangObject())
    ),
    categoryImage: dbCategoryImage || `${IMAGE_BASE}/${key}.jpg`,
    items: [],
    draft: JSON.parse(JSON.stringify(emptyDraft)),
  };

  setMenuCategories((prev) => [...prev, newCategory]);

  // ✅ Kullanıcı kategori nereye gitti diye düşünmesin:
  // kategori açılır, ürün ekleme formu açılır, sayfa oraya iner.
  setOpenCategoryId(newId);
  setActiveAddProductCategoryId(newId);

  setTimeout(() => {
    openAddProductForm(newId);
  }, 150);
};

  const updateCustomCategoryDraft = (lang, value) => {
    setCustomCategoryDraft((prev) => ({
      ...prev,
      [lang]: value,
    }));
  };

const addCustomCategory = () => {
  const hasAnyTitle = LANGS.some((l) =>
    customCategoryDraft[l.code]?.trim()
  );

  if (!hasAnyTitle) {
    showAppNotice("categoryRequired", "error");
    return;
  }

  const newId = Date.now();
  const customKey = `custom_${newId}`;

  const newCategory = {
    id: newId,
    key: customKey,
    customTitle: JSON.parse(JSON.stringify(customCategoryDraft)),
    categoryImage: `${IMAGE_BASE}/default.jpg`,
    items: [],
    draft: JSON.parse(JSON.stringify(emptyDraft)),
  };

  setMenuCategories((prev) => [...prev, newCategory]);

  setCustomCategoryDraft(makeLangObject());

  // ✅ Özel kategori eklenince de direkt ürün ekleme ekranına götür.
  setOpenCategoryId(newId);
  setActiveAddProductCategoryId(newId);

  setTimeout(() => {
    openAddProductForm(newId);
  }, 150);

  showAppNotice("saved");
};

  const updateCustomCategoryTitle = (cIndex, lang, value) => {
    setMenuCategories((prev) => {
      const copy = [...prev];
      const cat = copy[cIndex];

      if (!cat) return prev;

      copy[cIndex] = {
        ...cat,
        customTitle: {
          ...(cat.customTitle || makeLangObject()),
          [lang]: value,
        },
      };

      return copy;
    });
  };

  const updateCategoryImage = (cIndex, url) => {
    setMenuCategories((prev) => {
      const copy = [...prev];
      const cat = copy[cIndex];

      if (!cat) return prev;

      copy[cIndex] = {
        ...cat,
        categoryImage: url || "",
      };

      return copy;
    });
  };

const updateProductQuickField = (cIndex, iIndex, group, lang, value) => {
  setMenuCategories((prev) => {
    const copy = [...prev];
    const cat = copy[cIndex];

    if (!cat || !cat.items?.[iIndex]) return prev;

    const items = [...cat.items];
    const item = {
      ...items[iIndex],
      [group]: {
        ...(items[iIndex][group] || {}),
        [lang]: value,
      },
    };

    items[iIndex] = item;

    copy[cIndex] = {
      ...cat,
      items,
    };

    return copy;
  });
};

const saveProductQuickEdit = async () => {
  if (saving) return;

  await saveMenuToBackend();
};

 const addProduct = async (cIndex) => {
  if (saving) return;

  const currentCategory = menuCategories[cIndex];
  const draft = currentCategory?.draft;

  if (!currentCategory || !draft) return;

  const hasAnyTitle = LANGS.some((l) => draft.title[l.code]?.trim());

  if (!hasAnyTitle) {
    showAppNotice("titleRequired", "error");
    return;
  }

  const categoryId = currentCategory.id;

  // ✅ 200 ML / 400 ML / 1 LT gibi seçenekleri temizle.
  // Boş satırları kayda göndermeyiz, dolu olanları kaydederiz.
  const cleanPriceOptions = normalizePriceOptions(draft.price_options).filter(
    (opt) =>
      String(opt.label || "").trim() !== "" &&
      opt.price !== "" &&
      opt.price !== null &&
      opt.price !== undefined
  );

  const cleanProduct = {
    id: Date.now(),
    ...JSON.parse(JSON.stringify(draft)),
    price_options: cleanPriceOptions,
  };

  const nextCategories = menuCategories.map((cat, index) => {
    if (index !== cIndex) return cat;

    return {
      ...cat,
      items: [...cat.items, cleanProduct],
      draft: JSON.parse(JSON.stringify(emptyDraft)),
    };
  });

  setMenuCategories(nextCategories);

  setUploadResetKeys((prev) => ({
    ...prev,
    [categoryId]: Date.now(),
  }));

  // ✅ Artık ikinci kere "Menüyü Kaydet" gerekmez.
  // Ürün eklenir eklenmez backend'e kaydedilir.
  await saveMenuToBackend(nextCategories);

  setActiveAddProductCategoryId(null);
};

  const duplicateProduct = (cIndex, iIndex) => {
    const source = menuCategories[cIndex]?.items?.[iIndex];

    if (!source) return;

    setMenuCategories((prev) => {
      const copy = [...prev];
      const cat = copy[cIndex];

      if (!cat) return prev;

      const cloned = JSON.parse(JSON.stringify(source));

      cloned.id = Date.now();

      LANGS.forEach((l) => {
        if (cloned.title?.[l.code]) {
          cloned.title[l.code] = `${cloned.title[l.code]} - Kopya`;
        }
      });

      copy[cIndex] = {
        ...cat,
        items: [
          ...cat.items.slice(0, iIndex + 1),
          cloned,
          ...cat.items.slice(iIndex + 1),
        ],
      };

      return copy;
    });

    showAppNotice("saved");
  };


   const editProduct = (cIndex, iIndex) => {
    setEditingProduct({
      cIndex,
      iIndex,
      data: JSON.parse(JSON.stringify(menuCategories[cIndex].items[iIndex])),
    });
  };

  const updateEditingProduct = (group, lang, value) => {
    setEditingProduct((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        data: {
          ...prev.data,
          [group]: {
            ...prev.data[group],
            [lang]: value,
          },
        },
      };
    });
  };

   const updateEditingProductSingle = (field, value) => {
    setEditingProduct((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        data: {
          ...prev.data,
          [field]: value,
        },
      };
    });
  };

  // ✅ Yeni ürün ekleme formundaki fiyat seçenekleri
  const addDraftPriceOption = (cIndex) => {
    setMenuCategories((prev) => {
      const copy = [...prev];
      const cat = copy[cIndex];

      if (!cat) return prev;

      const draft = cat.draft || JSON.parse(JSON.stringify(emptyDraft));
      const currentOptions = normalizePriceOptions(draft.price_options);

      copy[cIndex] = {
        ...cat,
        draft: {
          ...draft,
          price_options: [
            ...currentOptions,
            {
              id: `opt-${Date.now()}-${Math.random()}`,
              label: "",
              price: "",
              currency: draft.currency?.[formLang] || "TRY",
            },
          ],
        },
      };

      return copy;
    });
  };

  const updateDraftPriceOption = (cIndex, optionIndex, field, value) => {
    setMenuCategories((prev) => {
      const copy = [...prev];
      const cat = copy[cIndex];

      if (!cat) return prev;

      const draft = cat.draft || JSON.parse(JSON.stringify(emptyDraft));
      const currentOptions = normalizePriceOptions(draft.price_options);

      const nextOptions = currentOptions.map((opt, index) =>
        index === optionIndex
          ? {
              ...opt,
              [field]: value,
            }
          : opt
      );

      copy[cIndex] = {
        ...cat,
        draft: {
          ...draft,
          price_options: nextOptions,
        },
      };

      return copy;
    });
  };

  const removeDraftPriceOption = (cIndex, optionIndex) => {
    setMenuCategories((prev) => {
      const copy = [...prev];
      const cat = copy[cIndex];

      if (!cat) return prev;

      const draft = cat.draft || JSON.parse(JSON.stringify(emptyDraft));

      copy[cIndex] = {
        ...cat,
        draft: {
          ...draft,
          price_options: normalizePriceOptions(draft.price_options).filter(
            (_, index) => index !== optionIndex
          ),
        },
      };

      return copy;
    });
  };


  const updateDraftComplianceLang = (cIndex, field, lang, value) => {
    setMenuCategories((prev) => {
      const copy = [...prev];
      const cat = copy[cIndex];

      if (!cat) return prev;

      const draft = cat.draft || JSON.parse(JSON.stringify(emptyDraft));
      const compliance = normalizeComplianceInfo(draft.compliance_info);

      copy[cIndex] = {
        ...cat,
        draft: {
          ...draft,
          compliance_info: {
            ...compliance,
            [field]: {
              ...(compliance[field] || makeLangObject()),
              [lang]: value,
            },
          },
        },
      };

      return copy;
    });
  };

  const updateDraftComplianceSingle = (cIndex, field, value) => {
    setMenuCategories((prev) => {
      const copy = [...prev];
      const cat = copy[cIndex];

      if (!cat) return prev;

      const draft = cat.draft || JSON.parse(JSON.stringify(emptyDraft));
      const compliance = normalizeComplianceInfo(draft.compliance_info);

      copy[cIndex] = {
        ...cat,
        draft: {
          ...draft,
          compliance_info: {
            ...compliance,
            [field]: value,
          },
        },
      };

      return copy;
    });
  };

  const toggleDraftComplianceTag = (cIndex, tagKey) => {
    setMenuCategories((prev) => {
      const copy = [...prev];
      const cat = copy[cIndex];

      if (!cat) return prev;

      const draft = cat.draft || JSON.parse(JSON.stringify(emptyDraft));
      const compliance = normalizeComplianceInfo(draft.compliance_info);
      const tags = Array.isArray(compliance.tags) ? compliance.tags : [];
      const active = tags.includes(tagKey);

      copy[cIndex] = {
        ...cat,
        draft: {
          ...draft,
          compliance_info: {
            ...compliance,
            tags: active
              ? tags.filter((x) => x !== tagKey)
              : [...tags, tagKey],
          },
        },
      };

      return copy;
    });
  };

  const updateEditingComplianceLang = (field, lang, value) => {
    setEditingProduct((prev) => {
      if (!prev) return prev;

      const compliance = normalizeComplianceInfo(prev.data.compliance_info);

      return {
        ...prev,
        data: {
          ...prev.data,
          compliance_info: {
            ...compliance,
            [field]: {
              ...(compliance[field] || makeLangObject()),
              [lang]: value,
            },
          },
        },
      };
    });
  };

  const updateEditingComplianceSingle = (field, value) => {
    setEditingProduct((prev) => {
      if (!prev) return prev;

      const compliance = normalizeComplianceInfo(prev.data.compliance_info);

      return {
        ...prev,
        data: {
          ...prev.data,
          compliance_info: {
            ...compliance,
            [field]: value,
          },
        },
      };
    });
  };

   const toggleEditingComplianceTag = (tagKey) => {
    setEditingProduct((prev) => {
      if (!prev) return prev;

      const compliance = normalizeComplianceInfo(prev.data.compliance_info);
      const tags = Array.isArray(compliance.tags) ? compliance.tags : [];
      const active = tags.includes(tagKey);

      return {
        ...prev,
        data: {
          ...prev.data,
          compliance_info: {
            ...compliance,
            tags: active
              ? tags.filter((x) => x !== tagKey)
              : [...tags, tagKey],
          },
        },
      };
    });
  };

  const applyDraftCalorieEstimate = (cIndex) => {
    setMenuCategories((prev) => {
      const copy = [...prev];
      const cat = copy[cIndex];

      if (!cat) return prev;

      const draft = cat.draft || JSON.parse(JSON.stringify(emptyDraft));
      const compliance = normalizeComplianceInfo(draft.compliance_info);
      const estimate = buildCalorieEstimate(draft, cat.key, formLang);

      copy[cIndex] = {
        ...cat,
        draft: {
          ...draft,
          compliance_info: {
            ...compliance,
            calories: estimate.calories,
            calories_estimated: true,
            calorie_note: {
              ...(compliance.calorie_note || makeLangObject()),
              [formLang]: estimate.note,
            },
          },
        },
      };

      return copy;
    });
  };

  const applyEditingCalorieEstimate = () => {
    setEditingProduct((prev) => {
      if (!prev) return prev;

      const categoryKey = menuCategories?.[prev.cIndex]?.key || "";
      const compliance = normalizeComplianceInfo(prev.data.compliance_info);
      const estimate = buildCalorieEstimate(prev.data, categoryKey, formLang);

      return {
        ...prev,
        data: {
          ...prev.data,
          compliance_info: {
            ...compliance,
            calories: estimate.calories,
            calories_estimated: true,
            calorie_note: {
              ...(compliance.calorie_note || makeLangObject()),
              [formLang]: estimate.note,
            },
          },
        },
      };
    });
  };

  // ✅ Ürün düzenleme modalındaki fiyat seçenekleri
  const addEditingPriceOption = () => {
    setEditingProduct((prev) => {
      if (!prev) return prev;

      const currentOptions = normalizePriceOptions(prev.data.price_options);

      return {
        ...prev,
        data: {
          ...prev.data,
          price_options: [
            ...currentOptions,
            {
              id: `opt-${Date.now()}-${Math.random()}`,
              label: "",
              price: "",
              currency: prev.data.currency?.[formLang] || "TRY",
            },
          ],
        },
      };
    });
  };

  const updateEditingPriceOption = (optionIndex, field, value) => {
    setEditingProduct((prev) => {
      if (!prev) return prev;

      const currentOptions = normalizePriceOptions(prev.data.price_options);

      return {
        ...prev,
        data: {
          ...prev.data,
          price_options: currentOptions.map((opt, index) =>
            index === optionIndex
              ? {
                  ...opt,
                  [field]: value,
                }
              : opt
          ),
        },
      };
    });
  };









  const removeEditingPriceOption = (optionIndex) => {
    setEditingProduct((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        data: {
          ...prev.data,
          price_options: normalizePriceOptions(prev.data.price_options).filter(
            (_, index) => index !== optionIndex
          ),
        },
      };
    });
  };

const saveEditingProduct = async () => {
  if (!editingProduct || saving) return;

 const cleanEditingData = {
  ...editingProduct.data,

  // ✅ Kalori, içerik, porsiyon, uyarı ve etiketler düzenlemede de kaybolmaz
  compliance_info: normalizeComplianceInfo(editingProduct.data.compliance_info),

  price_options: normalizePriceOptions(editingProduct.data.price_options).filter(
    (opt) =>
      String(opt.label || "").trim() !== "" &&
      opt.price !== "" &&
      opt.price !== null &&
      opt.price !== undefined
  ),
};

  const hasAnyTitle = LANGS.some((l) =>
    cleanEditingData.title?.[l.code]?.trim()
  );

  if (!hasAnyTitle) {
    showAppNotice("titleRequired", "error");
    return;
  }

  const nextCategories = menuCategories.map((cat, cIndex) => {
    if (cIndex !== editingProduct.cIndex) return cat;

    return {
      ...cat,
      items: cat.items.map((item, iIndex) =>
        iIndex === editingProduct.iIndex
          ? JSON.parse(JSON.stringify(cleanEditingData))
          : item
      ),
    };
  });

  setMenuCategories(nextCategories);
  setEditingProduct(null);

  await saveMenuToBackend(nextCategories);
};



const handleCategoryDragEnd = (event) => {
  const { active, over } = event;

  if (!over || active.id === over.id) return;

  setMenuCategories((prev) => {
    const oldIndex = prev.findIndex((cat) => `cat-${cat.id}` === active.id);
    const newIndex = prev.findIndex((cat) => `cat-${cat.id}` === over.id);

    if (oldIndex === -1 || newIndex === -1) return prev;

    return arrayMove(prev, oldIndex, newIndex);
  });
};

const handleProductDragEnd = (cIndex) => (event) => {
  const { active, over } = event;

  if (!over || active.id === over.id) return;

  setMenuCategories((prev) => {
    const copy = [...prev];
    const items = [...copy[cIndex].items];

    const oldIndex = items.findIndex((item) => `item-${item.id}` === active.id);
    const newIndex = items.findIndex((item) => `item-${item.id}` === over.id);

    if (oldIndex === -1 || newIndex === -1) return prev;

    copy[cIndex] = {
      ...copy[cIndex],
      items: arrayMove(items, oldIndex, newIndex),
    };

    return copy;
  });
};


const deleteProduct = async (cIndex, iIndex) => {
  const item = menuCategories[cIndex]?.items?.[iIndex];

  if (!item) return;

  if (item.id && String(item.id).length < 10) {
    try {
      const res = await fetch(`${API_BASE}/extra-items/${item.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        showAppNotice("error", "error");
        return;
      }
    } catch (err) {
      console.error("Silme hatası:", err);
      showAppNotice("connectionError", "error");
      return;
    }
  }

  setMenuCategories((prev) => {
    const copy = [...prev];

    if (!copy[cIndex]) return prev;

    copy[cIndex] = {
      ...copy[cIndex],
      items: copy[cIndex].items.filter((_, index) => index !== iIndex),
    };

    return copy;
  });

  showAppNotice("saved");
};

const requestDeleteProduct = (cIndex, iIndex) => {
  setConfirmBox({
    title: "Ürün silinsin mi?",
    description: "Bu ürün listeden kaldırılacak. Kaydedince menüden de silinir.",
    confirmText: "Sil",
    cancelText: "Vazgeç",
    onConfirm: () => deleteProduct(cIndex, iIndex),
  });
};

const deleteCategory = async (cIndex) => {
  const cat = menuCategories[cIndex];

  if (!cat) return;

  if (cat.id && String(cat.id).length < 10) {
    try {
      const res = await fetch(`${API_BASE}/extra-sections/${cat.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        showAppNotice("error", "error");
        return;
      }
    } catch (err) {
      console.error("Kategori silme hata:", err);
      showAppNotice("connectionError", "error");
      return;
    }
  }

  setMenuCategories((prev) => prev.filter((_, i) => i !== cIndex));
  showAppNotice("saved");
};

const requestDeleteCategory = (cIndex) => {
  setConfirmBox({
    title: "Kategori silinsin mi?",
    description: "Bu kategori ve içindeki ürünler panelden kaldırılacak.",
    confirmText: "Sil",
    cancelText: "Vazgeç",
    onConfirm: () => deleteCategory(cIndex),
  });
};




  if (loading) {
    return <div className="p-8 text-xl font-bold">Menü yükleniyor...</div>;
  }

return (
    <div className="w-full min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-100 p-3 md:p-8 overflow-x-hidden">

      {appNotice && (
        <div
          className={`fixed top-5 right-5 z-[9999] px-5 py-3 rounded-2xl shadow-2xl text-white font-bold transition ${
            appNotice.type === "error" ? "bg-red-600" : "bg-green-600"
          }`}
        >
          {appNotice.text}
        </div>
      )}

      {confirmBox && (
        <div className="fixed inset-0 z-[9998] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-xl font-extrabold text-gray-900">
              {confirmBox.title}
            </h3>

            <p className="text-gray-600 mt-2">
              {confirmBox.description}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <button
                type="button"
                onClick={async () => {
                  const action = confirmBox.onConfirm;
                  setConfirmBox(null);

                  if (action) {
                    await action();
                  }
                }}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 rounded-xl font-bold"
              >
                {confirmBox.confirmText || "Sil"}
              </button>

              <button
                type="button"
                onClick={() => setConfirmBox(null)}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 py-3 rounded-xl font-bold"
              >
                {confirmBox.cancelText || "Vazgeç"}
              </button>
            </div>
          </div>
        </div>
      )}

 <div className="bg-white/95 backdrop-blur rounded-3xl shadow-2xl border border-white/70 p-4 md:p-6 mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 sticky top-0 z-30">
  <div>
    <h1 className="text-2xl md:text-3xl font-extrabold text-gray-950">
      🍽 Menü Yönetimi
    </h1>

    <p className="text-gray-500 mt-1 text-sm md:text-base">
      Her dil için ayrı başlık, açıklama, fiyat ve para birimi yönetin.
    </p>
  </div>

<button
  type="button"
  onClick={() => saveMenuToBackend()}
  disabled={saving}
  className="w-full md:w-auto bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 py-4 rounded-2xl font-extrabold disabled:opacity-60 shadow-xl active:scale-95 transition"
>
  {saving ? "Kaydediliyor..." : "💾 Menüyü Kaydet"}
</button>
</div>



   <div className="bg-white/95 backdrop-blur rounded-3xl shadow-2xl border border-white/70 p-5 md:p-6 mb-6">
  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
    <div>
      <div className="font-extrabold text-gray-900 text-xl">
        🌍 Menü İçerik Dilleri
      </div>
      <div className="text-sm text-gray-500 mt-1">
        Hangi dilleri aktif ederseniz, ürün ekleme formunda sadece o diller görünür.
      </div>
    </div>

    <div className="text-sm font-bold text-orange-700 bg-orange-50 border border-orange-200 px-3 py-2 rounded-full">
      {menuLangs.length} dil aktif
    </div>
  </div>

    <div className="grid grid-cols-3 gap-2">
    {LANGS.map((l) => {
      const active = menuLangs.includes(l.code);

      return (
        <button
          key={l.code}
          type="button"
          onClick={() => {
            setMenuLangs((prev) => {
              if (prev.includes(l.code)) {
                if (prev.length === 1) return prev;

                const next = prev.filter((x) => x !== l.code);

                if (formLang === l.code) {
                  setFormLang(next[0] || "tr");
                }

                return next;
              }

              return [...prev, l.code];
            });
          }}
          className={`min-h-[82px] rounded-2xl border p-3 text-left transition-all duration-200 active:scale-[0.97] hover:-translate-y-0.5 ${
            active
              ? "bg-gradient-to-br from-orange-500 to-orange-700 text-white border-orange-600 shadow-lg"
              : "bg-white text-gray-700 border-gray-200 hover:border-orange-300 hover:bg-orange-50"
          }`}
        >
          <div className="flex items-center justify-between gap-2">
            <span
              className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-black ${
                active
                  ? "bg-white/20 text-white"
                  : "bg-gray-50 text-orange-700 border border-gray-100"
              }`}
            >
              {l.label}
            </span>

            <span
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black ${
                active
                  ? "bg-white text-orange-600"
                  : "bg-gray-100 text-gray-300"
              }`}
            >
              {active ? "✓" : ""}
            </span>
          </div>

          <div
            className={`mt-2 text-[12px] font-black leading-tight ${
              active ? "text-white" : "text-gray-900"
            }`}
          >
            {l.name}
          </div>

          <div
            className={`mt-1 text-[10px] font-bold ${
              active ? "text-white/80" : "text-gray-400"
            }`}
          >
            {active ? "Aktif" : "Pasif"}
          </div>
        </button>
      );
    })}
  </div>

  <p className="text-xs text-gray-500 mt-3">
    Not: En az 1 dil aktif kalmalıdır. Seçili diller müşteri menüsünde dil butonu olarak görünecektir.
  </p>
</div>

          <div className="bg-white rounded-2xl shadow border p-6 mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <label className="block font-bold mb-2">Hazır Kategori Ekle</label>
            <select
              className="p-3 border rounded-xl w-full"
              onChange={(e) => {
                addCategory(e.target.value);
                e.target.value = "";
              }}
            >
              <option value="">Kategori seç</option>
              {Object.entries(CATEGORY_DICT).map(([key, labels]) => (
                <option key={key} value={key}>
                  {labels[uiLang] || labels.tr}
                </option>
              ))}
            </select>

            <p className="text-xs text-gray-500 mt-2">
              Ana yemek, içecek, tatlı gibi hazır kategoriler.
            </p>
          </div>

          <div className="border rounded-2xl p-4 bg-gray-50">
            <div className="font-extrabold text-gray-900 mb-2">
              Özel Kategori Ekle
            </div>

            <div className="flex flex-wrap gap-2 mb-3">
              {LANGS.filter((l) => menuLangs.includes(l.code)).map((l) => (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => setFormLang(l.code)}
                  className={`px-3 py-2 rounded-full text-sm font-bold border ${
                    formLang === l.code
                      ? "bg-orange-600 text-white border-orange-600"
                      : "bg-white text-gray-700 border-gray-300"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>

            <input
              className="w-full p-3 border rounded-xl"
              placeholder={`Kategori adı (${formLang.toUpperCase()})`}
              value={customCategoryDraft[formLang] || ""}
              onChange={(e) =>
                updateCustomCategoryDraft(formLang, e.target.value)
              }
            />

            <button
              type="button"
              onClick={addCustomCategory}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white py-3 rounded-xl font-bold mt-3"
            >
              ➕ Özel Kategori Ekle
            </button>

            <p className="text-xs text-gray-500 mt-2">
              Örnek: Kampanyalar, Menü Fırsatları, Öğrenci Menüsü, Aile Paketi.
            </p>
          </div>
        </div>
      </div>



      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleCategoryDragEnd}
      >
        <SortableContext
          items={menuCategories.map((cat) => `cat-${cat.id}`)}
          strategy={verticalListSortingStrategy}
        >
          {menuCategories.map((cat, cIndex) => (
            <SortableCategoryBox key={cat.id} cat={cat}>
              {({ dragHandleProps }) => (
                      <div className="bg-white/95 backdrop-blur rounded-3xl shadow-xl border border-white/70 mb-5 overflow-hidden">
             <div
   className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-4 md:p-5 bg-gradient-to-r from-slate-50 to-orange-50 cursor-pointer"
  onClick={() =>
    setOpenCategoryId(openCategoryId === cat.id ? null : cat.id)
  }
>
<div className="flex items-center gap-3 w-full min-w-0">
                      <button
                        type="button"
                        {...dragHandleProps}
                        onClick={(e) => e.stopPropagation()}
                     className="w-10 h-10 rounded-xl border bg-white text-gray-500 hover:text-orange-600 hover:border-orange-300 flex items-center justify-center font-extrabold cursor-grab active:cursor-grabbing touch-none select-none"
style={{ touchAction: "none" }}
title="Sürükle bırak ile sırala"
                      >
                        ☰
                      </button>

                                            <div className="flex items-center gap-3">
                                              <div className="w-14 h-14 rounded-2xl border bg-white overflow-hidden flex items-center justify-center flex-shrink-0">
                          <img
                            src={resolvePanelCategoryImage(cat)}
                            alt=""
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.src = `${IMAGE_BASE}/default.jpg`;
                            }}
                          />
                        </div>

                        <div>
                          <h2 className="text-xl font-extrabold">
                            {getCategoryTitle(cat, uiLang)}
                          </h2>
                          <div className="text-sm text-gray-500">
                            {cat.items.length} ürün
                          </div>
                        </div>
                      </div>
                    </div>

 <div className="w-full md:w-auto grid grid-cols-2 md:flex md:items-center md:justify-end gap-2">
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      openAddProductForm(cat.id);
    }}
    className="col-span-2 md:col-span-1 h-14 md:h-12 px-5 rounded-2xl bg-orange-600 hover:bg-orange-700 text-white text-base md:text-sm font-black shadow-md active:scale-95 transition flex items-center justify-center gap-2 text-center"
  >
    <span>➕</span>
    <span>Ürün Ekle</span>
  </button>

  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      setOpenCategoryId(openCategoryId === cat.id ? null : cat.id);
    }}
    className="h-12 px-4 rounded-2xl bg-white hover:bg-gray-50 border text-gray-600 text-sm font-black active:scale-95 transition flex items-center justify-center text-center"
  >
    {openCategoryId === cat.id ? "Kapat" : "Aç"}
  </button>

  <button
    type="button"
    className="h-12 px-4 rounded-2xl bg-red-50 hover:bg-red-100 text-red-700 border border-red-100 text-sm font-black active:scale-95 transition flex items-center justify-center text-center"
    onClick={(e) => {
      e.stopPropagation();
      requestDeleteCategory(cIndex);
    }}
  >
    Sil
  </button>
</div>
                  </div>

           {openCategoryId === cat.id && (
                    <div className="p-3 md:p-5 flex flex-col gap-4">


                      <div className="order-4 bg-white border rounded-2xl p-4">
                        <div className="font-extrabold text-gray-900 mb-2">
                          ⚙️ Gelişmiş Ayar: Ana Kategori Görseli
                        </div>

                        <p className="text-xs text-gray-500 mb-3">
                          Bu görsel müşteri menüsünde en üst kategori resmi olarak kullanılır.
                        </p>

                        <ResimUpload
                          key={`category-image-${cat.id}`}
                          label="Ana Kategori Görseli"
                          onUpload={(url) => updateCategoryImage(cIndex, url)}
                        />

                   <div className="mt-3 flex items-center gap-4">
  <div className="w-32 h-24 rounded-2xl border bg-gray-50 overflow-hidden flex items-center justify-center">
    <img
      src={resolvePanelCategoryImage(cat)}
      alt=""
      className="w-full h-full object-cover"
      onError={(e) => {
        e.currentTarget.src = `${IMAGE_BASE}/default.jpg`;
      }}
    />
  </div>

  {cat.categoryImage && !String(cat.categoryImage).includes("/menu/images/") && (
    <button
      type="button"
      onClick={() => updateCategoryImage(cIndex, "")}
      className="text-red-600 font-bold text-sm"
    >
      Görseli Kaldır
    </button>
  )}
</div>
                      </div>

                    <div className="order-5 bg-orange-50 border border-orange-200 rounded-2xl p-4">
  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
    <div>
      <div className="font-extrabold text-gray-900">
        Ana Kategori Adı
      </div>

      <p className="text-xs text-gray-500 mt-1">
        Hazır kategori olsa bile buradan görünen adı değiştirebilirsiniz.
        Örnek: “Dürüm & Kebap” yerine sadece “Dürüm”.
      </p>
    </div>

    <div className="flex flex-wrap gap-2">
      {LANGS.filter((l) => menuLangs.includes(l.code)).map((l) => (
        <button
          key={l.code}
          type="button"
          onClick={() => setFormLang(l.code)}
          className={`px-3 py-2 rounded-full text-xs font-bold border ${
            formLang === l.code
              ? "bg-orange-600 text-white border-orange-600"
              : "bg-white text-gray-700 border-gray-300"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  </div>

  <input
    className="w-full p-3 border rounded-xl"
    placeholder={`Kategori adı (${formLang.toUpperCase()})`}
    value={
      cat.customTitle?.[formLang] ||
      CATEGORY_DICT[cat.key]?.[formLang] ||
      ""
    }
    onChange={(e) =>
      updateCustomCategoryTitle(cIndex, formLang, e.target.value)
    }
  />

  <p className="text-xs text-gray-500 mt-2">
    Kaydedince müşteri QR menüsünde bu isim görünür.
  </p>
</div>



{activeAddProductCategoryId !== cat.id ? (
<div className="order-1 rounded-3xl border border-dashed border-orange-200 bg-gradient-to-br from-orange-50 via-white to-blue-50 p-5 md:p-6">
  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5 text-center md:text-left">
      <div>
        <div className="text-lg md:text-xl font-black text-gray-900">
          ➕ Yeni Ürün / Hizmet Ekle
        </div>

        <p className="text-sm text-gray-500 mt-1">
          Ürün adı, fiyat, görsel, açıklama ve porsiyon seçeneklerini tek ekrandan ekleyin.
        </p>
      </div>

      <button
        type="button"
        onClick={() => openAddProductForm(cat.id)}
   className="w-full max-w-md mx-auto md:mx-0 md:w-auto md:min-w-[300px] h-16 bg-orange-600 hover:bg-orange-700 text-white px-6 rounded-3xl font-black shadow-lg active:scale-95 transition flex items-center justify-center text-center text-base md:text-lg"
      >
        Ürün Ekleme Ekranını Aç
      </button>
    </div>
  </div>
) : (
  <div
    ref={(el) => {
      addProductFormRefs.current[cat.id] = el;
    }}
    className="order-1 scroll-mt-28 rounded-3xl border border-orange-200 bg-white shadow-xl overflow-hidden"
  >
    <div className="bg-gradient-to-r from-orange-600 to-orange-500 text-white p-4 md:p-5">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="text-xl md:text-2xl font-black">
            ➕ Yeni Ürün / Hizmet Ekle
          </div>

          <p className="text-sm text-white/85 mt-1">
            Önce dili seçin, ürün adını ve fiyatı yazın. İsterseniz görsel, açıklama ve fiyat seçenekleri ekleyin.
          </p>
        </div>

        <button
          type="button"
          onClick={() => closeAddProductForm(cat.id)}
          className="bg-white/15 hover:bg-white/25 border border-white/25 px-4 py-3 rounded-2xl font-black text-sm"
        >
          Kapat
        </button>
      </div>
    </div>

    <div className="p-4 md:p-5">
      <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4 mb-4">
        <div className="font-extrabold text-gray-900">
          1️⃣ Düzenlenecek Dili Seç
        </div>

        <p className="text-xs text-gray-500 mt-1 mb-3">
          Ürün adını, fiyatı ve açıklamayı hangi dilde girecekseniz önce o dili seçin.
        </p>

        <LangMiniButtons
          langs={LANGS.filter((l) => menuLangs.includes(l.code))}
          activeLang={formLang}
          onSelect={setFormLang}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[320px_1fr] gap-4">
        <div className="bg-gray-50 border rounded-2xl p-4">
          <div className="font-extrabold text-gray-900 mb-2">
            2️⃣ Görsel ve Etiketler
          </div>

          <p className="text-xs text-gray-500 mb-3">
            Görsel isteğe bağlıdır. Boş bırakırsanız sistem varsayılan görseli kullanır.
          </p>

          <ResimUpload
            key={`${cat.id}-upload-${uploadResetKeys[cat.id] || 0}`}
            label="Ürün Görseli"
            onUpload={(url) => updateDraftSingle(cIndex, "image", url)}
          />

          {cat.draft.image ? (
            <div className="w-full h-44 bg-white rounded-2xl border mt-3 p-2 flex items-center justify-center overflow-hidden">
              <img
                src={cat.draft.image}
                alt=""
                className="w-full h-full object-contain"
              />
            </div>
          ) : (
            <div className="w-full h-32 bg-white border border-dashed rounded-2xl mt-3 flex items-center justify-center text-gray-400 text-sm font-bold">
              Görsel seçilmedi
            </div>
          )}

          <label className="flex items-center justify-between gap-3 font-bold border rounded-xl p-4 bg-white mt-4">
            <span>⭐ Trend Ürün Olsun</span>

            <input
              type="checkbox"
              checked={cat.draft.is_trend === true}
              onChange={(e) =>
                updateDraftSingle(cIndex, "is_trend", e.target.checked)
              }
              className="accent-orange-600 w-5 h-5"
            />
          </label>

          <div className="mt-4">
            <div className="text-xs font-black text-gray-600 mb-2">
              Alerjen Bilgisi
            </div>

            <div className="flex flex-wrap gap-2">
              {ALLERGEN_KEYS.map((key) => {
                const active = cat.draft.allergens.includes(key);

                return (
                  <button
                    key={key}
                    type="button"
                    className={`px-3 py-2 rounded-full text-xs border font-bold ${
                      active
                        ? "bg-orange-600 text-white border-orange-600"
                        : "bg-white text-gray-700 border-gray-200"
                    }`}
                    onClick={() => {
                      const list = cat.draft.allergens || [];

                      updateDraftSingle(
                        cIndex,
                        "allergens",
                        active
                          ? list.filter((x) => x !== key)
                          : [...list, key]
                      );
                    }}
                  >
                    {ALLERGEN_LABELS[key]}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-2xl p-4">
          <div className="font-extrabold text-gray-900 mb-3">
            3️⃣ Ürün Bilgisi
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-extrabold text-gray-600 mb-1">
                Ürün adı / Hizmet adı ({formLang.toUpperCase()})
              </label>

              <input
                ref={(el) => {
                  productNameInputRefs.current[cat.id] = el;
                }}
                placeholder="Örn: Tavuk Şinitzel"
                className="w-full p-4 border rounded-xl text-base font-bold focus:outline-none focus:ring-4 focus:ring-orange-100 focus:border-orange-400"
                value={cat.draft.title[formLang] || ""}
                onChange={(e) =>
                  updateDraft(cIndex, "title", formLang, e.target.value)
                }
              />
            </div>

<div className="border border-emerald-100 bg-emerald-50 rounded-2xl p-4">
  <div className="flex items-start justify-between gap-3 mb-3">
    <div>
      <div className="font-extrabold text-gray-900">
        ✅ Mevzuata Uyumlu Menü Bilgileri
      </div>

     <p className="text-xs text-gray-500 mt-1">
  İçindekiler, kalori, porsiyon ve ürün bazlı özel uyarılar QR menüde gösterilir.
</p>
    </div>

    <span className="shrink-0 text-[11px] font-black bg-white text-emerald-700 border border-emerald-200 px-3 py-1 rounded-full">
      Yeni
    </span>
  </div>

  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
    <div>
      <label className="block text-xs font-extrabold text-gray-600 mb-1">
        Kalori
      </label>

  <div className="flex flex-col sm:flex-row gap-2">
  <input
    type="number"
    inputMode="numeric"
    placeholder="Örn: 740"
    className="w-full p-3 border rounded-xl bg-white font-bold"
    value={normalizeComplianceInfo(cat.draft.compliance_info).calories || ""}
    onChange={(e) =>
      updateDraftComplianceSingle(cIndex, "calories", e.target.value)
    }
  />

  <button
    type="button"
    onClick={() => applyDraftCalorieEstimate(cIndex)}
    className="shrink-0 px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-xs font-black shadow hover:shadow-lg active:scale-95"
  >
    ✨ Tahmini Oluştur
  </button>
</div>

<p className="text-[11px] text-gray-500 mt-1">
  Manuel girebilir veya sistemin önerdiği tahmini kaloriyi kullanabilirsiniz.
</p>

{normalizeComplianceInfo(cat.draft.compliance_info).calories_estimated && (
  <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-xl p-2 mt-2">
    {normalizeComplianceInfo(cat.draft.compliance_info).calorie_note?.[
      formLang
    ] || CALORIE_ESTIMATE_NOTE.tr}
  </p>
)}



    </div>

    <div>
      <label className="block text-xs font-extrabold text-gray-600 mb-1">
        Porsiyon / Gramaj ({formLang.toUpperCase()})
      </label>

      <input
        placeholder="Örn: 1 porsiyon / 250 gr / 300 ml"
        className="w-full p-3 border rounded-xl bg-white font-bold"
        value={
          normalizeComplianceInfo(cat.draft.compliance_info).portion?.[
            formLang
          ] || ""
        }
        onChange={(e) =>
          updateDraftComplianceLang(
            cIndex,
            "portion",
            formLang,
            e.target.value
          )
        }
      />
    </div>
  </div>

  <div className="mt-3">
    <label className="block text-xs font-extrabold text-gray-600 mb-1">
      İçindekiler / Bileşenler ({formLang.toUpperCase()})
    </label>

    <textarea
      placeholder="Örn: Dana eti, lavaş, domates, soğan, yoğurt sos"
      className="w-full p-3 border rounded-xl bg-white"
      rows={3}
      value={
        normalizeComplianceInfo(cat.draft.compliance_info).ingredients?.[
          formLang
        ] || ""
      }
      onChange={(e) =>
        updateDraftComplianceLang(
          cIndex,
          "ingredients",
          formLang,
          e.target.value
        )
      }
    />
  </div>

  <div className="mt-3">
    <label className="block text-xs font-extrabold text-gray-600 mb-1">
      Özel Uyarı ({formLang.toUpperCase()})
    </label>

    <textarea
      placeholder="Örn: Acı içerir, eser miktarda fındık içerebilir, pişirme yöntemi değişebilir."
      className="w-full p-3 border rounded-xl bg-white"
      rows={2}
      value={
        normalizeComplianceInfo(cat.draft.compliance_info).warning?.[
          formLang
        ] || ""
      }
      onChange={(e) =>
        updateDraftComplianceLang(
          cIndex,
          "warning",
          formLang,
          e.target.value
        )
      }
    />
  </div>

  <div className="mt-4">
    <div className="text-xs font-black text-gray-600 mb-2">
      Beslenme / İçerik Etiketleri
    </div>

    <div className="flex flex-wrap gap-2">
      {DIETARY_TAGS.map((tag) => {
        const tags =
          normalizeComplianceInfo(cat.draft.compliance_info).tags || [];
        const active = tags.includes(tag.key);

        return (
          <button
            key={tag.key}
            type="button"
            onClick={() => toggleDraftComplianceTag(cIndex, tag.key)}
            className={`px-3 py-2 rounded-full text-xs border font-bold ${
              active
                ? "bg-emerald-600 text-white border-emerald-600"
                : "bg-white text-gray-700 border-gray-200"
            }`}
          >
            {tag.label}
          </button>
        );
      })}
    </div>
  </div>

  <p className="text-[11px] text-gray-500 mt-4 bg-white border border-dashed rounded-xl p-3">
    QR menüyü kullanamayan misafirler için işletme bu bilgileri personel üzerinden de paylaşmalıdır.
  </p>
</div>



            <div>
              <label className="block text-xs font-extrabold text-orange-700 mb-1">
                Ana Fiyat ({formLang.toUpperCase()})
              </label>

              <div className="grid grid-cols-[1fr_96px] gap-2">
                <input
                  type="number"
                  inputMode="decimal"
                  placeholder="Fiyat"
                  className="w-full p-4 border border-orange-200 rounded-xl text-xl font-extrabold text-orange-700 bg-orange-50 focus:outline-none focus:ring-4 focus:ring-orange-100"
                  value={cat.draft.price[formLang] || ""}
                  onChange={(e) =>
                    updateDraft(cIndex, "price", formLang, e.target.value)
                  }
                />

                <select
                  className="w-full p-3 border border-orange-200 rounded-xl font-bold bg-white"
                  value={cat.draft.currency[formLang] || "TRY"}
                  onChange={(e) =>
                    updateDraft(cIndex, "currency", formLang, e.target.value)
                  }
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              <p className="text-xs text-gray-500 mt-2">
                Üründe boy / porsiyon seçeneği yoksa sadece bu fiyat görünür.
              </p>
            </div>

            <div className="border border-blue-100 bg-blue-50 rounded-2xl p-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
                <div>
                  <div className="font-extrabold text-gray-900">
                    Boy / Porsiyon / Fiyat Seçenekleri
                  </div>

                  <p className="text-xs text-gray-500 mt-1">
                    Örn: 300 ML, 500 ML, 1 LT, küçük, büyük, duble. Boş bırakırsanız tek fiyat çalışır.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => addDraftPriceOption(cIndex)}
                  className="w-full md:w-auto shrink-0 px-4 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-extrabold"
                >
                  + Seçenek Ekle
                </button>
              </div>

              {normalizePriceOptions(cat.draft.price_options).length === 0 ? (
                <div className="text-xs text-gray-500 bg-white border border-dashed rounded-xl p-3">
                  Henüz seçenek eklenmedi. Ürün tek fiyatla çalışır.
                </div>
              ) : (
                <div className="space-y-2">
                  {normalizePriceOptions(cat.draft.price_options).map(
                    (opt, optIndex) => (
                      <div
                        key={opt.id || optIndex}
                        className="grid grid-cols-1 md:grid-cols-[1fr_130px_100px_80px] gap-2 bg-white border rounded-xl p-2"
                      >
                        <input
                          placeholder="Örn: 300 ML"
                          className="w-full p-3 border rounded-xl text-sm font-bold"
                          value={opt.label || ""}
                          onChange={(e) =>
                            updateDraftPriceOption(
                              cIndex,
                              optIndex,
                              "label",
                              e.target.value
                            )
                          }
                        />

                        <input
                          type="number"
                          inputMode="decimal"
                          placeholder="Fiyat"
                          className="w-full p-3 border border-orange-200 rounded-xl text-sm font-extrabold text-orange-700 bg-orange-50"
                          value={opt.price || ""}
                          onChange={(e) =>
                            updateDraftPriceOption(
                              cIndex,
                              optIndex,
                              "price",
                              e.target.value
                            )
                          }
                        />

                        <select
                          className="w-full p-3 border rounded-xl text-sm font-bold bg-white"
                          value={opt.currency || "TRY"}
                          onChange={(e) =>
                            updateDraftPriceOption(
                              cIndex,
                              optIndex,
                              "currency",
                              e.target.value
                            )
                          }
                        >
                          {CURRENCIES.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>

                        <button
                          type="button"
                          onClick={() => removeDraftPriceOption(cIndex, optIndex)}
                          className="w-full px-3 py-2 rounded-xl bg-red-50 text-red-700 border border-red-100 text-xs font-extrabold"
                        >
                          Sil
                        </button>
                      </div>
                    )
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-extrabold text-gray-600 mb-1">
             Kısa Menü Açıklaması ({formLang.toUpperCase()})
              </label>

              <textarea
                placeholder="Ürün açıklaması yazın..."
                className="w-full p-4 border rounded-xl focus:outline-none focus:ring-4 focus:ring-orange-100 focus:border-orange-400"
                rows={4}
                value={cat.draft.description[formLang] || ""}
                onChange={(e) =>
                  updateDraft(cIndex, "description", formLang, e.target.value)
                }
              />
            </div>

            <div className="sticky bottom-0 bg-white pt-3 border-t">
          <button
  type="button"
  onClick={() => addProduct(cIndex)}
  disabled={saving}
  className="w-full bg-green-600 hover:bg-green-700 text-white py-4 rounded-2xl font-black shadow-lg active:scale-[0.98] transition disabled:opacity-60"
>
  {saving ? "Kaydediliyor..." : "✅ Ürünü Kaydet"}
</button>

<p className="text-xs text-gray-500 text-center mt-2">
  Bu butona basınca ürün, fiyat ve 200 ML / 400 ML gibi seçenekler direkt kaydedilir.
</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
)}


<div className="order-2 bg-white border rounded-3xl shadow-sm overflow-hidden">
                        <div className="p-4 bg-gradient-to-r from-orange-50 to-white border-b">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <div className="text-lg md:text-xl font-black text-gray-900">
                                Ürün & Fiyat Tablosu
                              </div>
                            <p className="text-xs md:text-sm text-gray-500 mt-1">
  Fiyatı buradan hızlıca değiştirin, sonra aynı satırdaki “Hızlı Kaydet” butonuna basın.
  Detaylı ürün bilgisi ve 200 ML / 400 ML gibi seçenekler için “Detaylı Düzenle”yi kullanın.
</p>
                            </div>

                            <select
                              className="h-10 rounded-xl border border-orange-100 px-3 text-sm font-black bg-orange-50 text-orange-700"
                              value={formLang}
                              onChange={(e) => setFormLang(e.target.value)}
                            >
                              {LANGS.filter((l) => menuLangs.includes(l.code)).map((l) => (
                                <option key={l.code} value={l.code}>
                                  {l.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleProductDragEnd(cIndex)}
                      >
                        <SortableContext
                          items={cat.items.map((item) => `item-${item.id}`)}
                          strategy={verticalListSortingStrategy}
                        >
                          {cat.items.map((item, iIndex) => (
                            <SortableProductBox key={item.id} item={item}>
                              {({ dragHandleProps }) => (
                               


                                <div className="bg-white px-3 py-3 border-b last:border-b-0 hover:bg-orange-50/40 transition-all">
                 <div className="grid grid-cols-1 2xl:grid-cols-[52px_minmax(260px,1.4fr)_170px_140px_520px] xl:grid-cols-[52px_minmax(240px,1.4fr)_160px_130px_480px] gap-4 xl:items-center">

         <div className="flex items-center justify-between md:justify-center">
                                      <button
                                        type="button"
                                        {...dragHandleProps}
                                        className="w-10 h-10 rounded-xl border bg-white text-gray-500 hover:text-orange-600 hover:border-orange-300 flex items-center justify-center font-black cursor-grab active:cursor-grabbing touch-none select-none"
                                        style={{ touchAction: "none" }}
                                        title="Ürünü sürükle bırak ile sırala"
                                      >
                                        ☰
                                      </button>

                                      <span className="md:hidden text-xs font-black text-gray-400">
                                        #{iIndex + 1}
                                      </span>
                                    </div>

                                    <div className="flex items-center gap-3 min-w-0">
                                      <div className="w-14 h-14 rounded-2xl bg-gray-50 border overflow-hidden flex items-center justify-center shrink-0">
                                        {item.image ? (
                                          <img
                                            src={item.image}
                                            alt=""
                                            className="w-full h-full object-cover"
                                          />
                                        ) : (
                                          <span className="text-gray-400">🖼️</span>
                                        )}
                                      </div>

                                      <div className="min-w-0">
                                        <div className="font-black text-gray-900 truncate">
                                          {getLangValue(item.title, formLang) || "İsimsiz ürün"}
                                        </div>

                                        <div className="text-xs text-gray-500 line-clamp-2 mt-1">
                                          {getLangValue(item.description, formLang) || "Açıklama yok"}
                                        </div>

                                        {item.is_trend && (
                                          <div className="inline-flex mt-1 px-2 py-1 rounded-full bg-orange-100 text-orange-700 text-[10px] font-black">
                                            ⭐ Trend
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    <div>
                                      <label className="md:hidden block text-[11px] font-black text-gray-500 mb-1">
                                        Fiyat
                                      </label>

                                      <input
                                        type="number"
                                        inputMode="decimal"
                                        placeholder="Fiyat"
                                        className="w-full h-11 px-3 border border-orange-100 rounded-xl font-black text-orange-700 bg-orange-50"
                                        value={item.price?.[formLang] || ""}
                                        onChange={(e) =>
                                          updateProductQuickField(
                                            cIndex,
                                            iIndex,
                                            "price",
                                            formLang,
                                            e.target.value
                                          )
                                        }
                                      />
                                    </div>

                                    <div>
                                      <label className="md:hidden block text-[11px] font-black text-gray-500 mb-1">
                                        Para
                                      </label>

                                      <select
                                        className="w-full h-11 px-3 border border-gray-200 rounded-xl font-black bg-white"
                                        value={item.currency?.[formLang] || "TRY"}
                                        onChange={(e) =>
                                          updateProductQuickField(
                                            cIndex,
                                            iIndex,
                                            "currency",
                                            formLang,
                                            e.target.value
                                          )
                                        }
                                      >
                                        {CURRENCIES.map((c) => (
                                          <option key={c} value={c}>
                                            {c}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
<div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 w-full xl:min-w-[480px] 2xl:min-w-[520px]">
  <button
    type="button"
    className="h-[52px] md:h-14 px-3 md:px-4 bg-green-600 hover:bg-green-700 active:scale-95 transition-all text-white text-xs md:text-sm font-black rounded-2xl shadow-sm disabled:opacity-60 flex items-center justify-center text-center leading-tight whitespace-nowrap"
    disabled={saving}
    onClick={() => saveProductQuickEdit()}
  >
    {saving ? "Kaydediliyor..." : "Hızlı Kaydet"}
  </button>

  <button
    type="button"
    className="h-[52px] md:h-14 px-3 md:px-4 bg-orange-600 hover:bg-orange-700 active:scale-95 transition-all text-white text-xs md:text-sm font-black rounded-2xl shadow-sm flex items-center justify-center text-center leading-tight whitespace-nowrap"
    onClick={() => editProduct(cIndex, iIndex)}
  >
    Detaylı Düzenle
  </button>

  <button
    type="button"
    className="h-[52px] md:h-14 px-3 md:px-4 bg-blue-50 hover:bg-blue-100 active:scale-95 transition-all text-blue-700 text-xs md:text-sm font-black rounded-2xl border border-blue-100 flex items-center justify-center text-center leading-tight whitespace-nowrap"
    onClick={() => duplicateProduct(cIndex, iIndex)}
  >
    Kopyala
  </button>

  <button
    type="button"
    className="h-[52px] md:h-14 px-3 md:px-4 bg-red-50 hover:bg-red-100 active:scale-95 transition-all text-red-700 text-xs md:text-sm font-black rounded-2xl border border-red-100 flex items-center justify-center text-center leading-tight whitespace-nowrap"
    onClick={() => requestDeleteProduct(cIndex, iIndex)}
  >
    Sil
  </button>
</div>


                                  </div>
                                </div>
                              )}
                            </SortableProductBox>
                          ))}
                        </SortableContext>
                      </DndContext>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </SortableCategoryBox>
          ))}
        </SortableContext>
      </DndContext>

{editingProduct && (
  <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
      <div className="sticky top-0 bg-white border-b p-5 rounded-t-3xl flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-extrabold text-gray-900">
            Ürün Düzenle
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Ürün bilgilerini düzenleyin, kaydedince listede aynı yerinde güncellenir.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setEditingProduct(null)}
          className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 font-extrabold"
        >
          ×
        </button>
      </div>

      <div className="p-5">


     <div className="bg-white border border-orange-200 rounded-2xl p-4 mb-4">
  <div className="font-extrabold text-gray-900">
    1️⃣ Önce Dili Seç
  </div>

  <p className="text-xs text-gray-500 mt-1 mb-3">
    Hangi dilde ürün adı ve fiyat yazacaksanız önce buradan seçin.
  </p>

  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
    {LANGS.filter((l) => menuLangs.includes(l.code)).map((l) => (
      <button
        key={l.code}
        type="button"
        onClick={() => setFormLang(l.code)}
        className={`py-3 rounded-xl text-sm font-extrabold border ${
          formLang === l.code
            ? "bg-orange-600 text-white border-orange-600 shadow"
            : "bg-white text-gray-700 border-gray-300"
        }`}
      >
        {l.label}
      </button>
    ))}
  </div>
</div>

        <ResimUpload
          key={`edit-upload-${editingProduct.data.id}`}
          label="Ürün Görseli"
          onUpload={(url) => updateEditingProductSingle("image", url)}
        />

        {editingProduct.data.image && (
          <div className="w-32 h-32 bg-gray-50 rounded-2xl border mt-3 p-2 flex items-center justify-center">
            <img
              src={editingProduct.data.image}
              alt=""
              className="w-full h-full object-contain"
            />
          </div>
        )}


<div className="bg-white border rounded-2xl p-4 mt-5">
  <div className="font-extrabold text-gray-900 mb-3">
    2️⃣ Ürün ve Fiyat
  </div>

  <div className="space-y-3">
    <div>
      <label className="block text-xs font-extrabold text-gray-600 mb-1">
        Ürün adı / Hizmet adı ({formLang.toUpperCase()})
      </label>

      <input
        placeholder="Ürün adı"
        className="w-full p-4 border rounded-xl text-base font-bold"
        value={editingProduct.data.title?.[formLang] || ""}
        onChange={(e) =>
          updateEditingProduct("title", formLang, e.target.value)
        }
      />
    </div>

    <div>
      <label className="block text-xs font-extrabold text-orange-700 mb-1">
        Fiyat ({formLang.toUpperCase()})
      </label>

      <div className="grid grid-cols-[1fr_96px] gap-2">
        <input
          type="number"
          inputMode="decimal"
          placeholder="Fiyat"
          className="w-full p-4 border border-orange-200 rounded-xl text-xl font-extrabold text-orange-700 bg-orange-50"
          value={editingProduct.data.price?.[formLang] || ""}
          onChange={(e) =>
            updateEditingProduct("price", formLang, e.target.value)
          }
        />

               <select
          className="w-full p-3 border border-orange-200 rounded-xl font-bold bg-white"
          value={editingProduct.data.currency?.[formLang] || "TRY"}
          onChange={(e) =>
            updateEditingProduct("currency", formLang, e.target.value)
          }
        >
          {CURRENCIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>
    </div>

    {/* ✅ Çoklu fiyat seçenekleri - Düzenleme */}
    <div className="mt-4 border border-blue-100 bg-blue-50 rounded-2xl p-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <div className="font-extrabold text-gray-900">
            Boy / Porsiyon / Fiyat Seçenekleri
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Örn: 300 ML, 500 ML, Şişe 500 ML, 1 LT. Boşsa ürün tek fiyatla çalışır.
          </p>
        </div>

        <button
          type="button"
          onClick={addEditingPriceOption}
          className="shrink-0 px-3 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-extrabold"
        >
          + Seçenek
        </button>
      </div>

      {normalizePriceOptions(editingProduct.data.price_options).length === 0 ? (
        <div className="text-xs text-gray-500 bg-white border border-dashed rounded-xl p-3">
          Henüz seçenek eklenmedi. Ürün tek fiyatla çalışır.
        </div>
      ) : (
        <div className="space-y-2">
          {normalizePriceOptions(editingProduct.data.price_options).map(
            (opt, optIndex) => (
              <div
                key={opt.id || optIndex}
                className="grid grid-cols-1 md:grid-cols-[1fr_130px_100px_80px] gap-2 bg-white border rounded-xl p-2"
              >
                <input
                  placeholder="Örn: 300 ML"
                  className="w-full p-3 border rounded-xl text-sm font-bold"
                  value={opt.label || ""}
                  onChange={(e) =>
                    updateEditingPriceOption(optIndex, "label", e.target.value)
                  }
                />

                <input
                  type="number"
                  inputMode="decimal"
                  placeholder="Fiyat"
                  className="w-full p-3 border border-orange-200 rounded-xl text-sm font-extrabold text-orange-700 bg-orange-50"
                  value={opt.price || ""}
                  onChange={(e) =>
                    updateEditingPriceOption(optIndex, "price", e.target.value)
                  }
                />

                <select
                  className="w-full p-3 border rounded-xl text-sm font-bold bg-white"
                  value={opt.currency || "TRY"}
                  onChange={(e) =>
                    updateEditingPriceOption(optIndex, "currency", e.target.value)
                  }
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  onClick={() => removeEditingPriceOption(optIndex)}
                  className="w-full px-3 py-2 rounded-xl bg-red-50 text-red-700 border border-red-100 text-xs font-extrabold"
                >
                  Sil
                </button>
              </div>
            )
          )}
        </div>
      )}
    </div>

    <label className="flex items-center justify-between gap-3 font-bold border rounded-xl p-4 bg-gray-50">
      <span>⭐ Trend Ürün Olsun</span>

      <input
        type="checkbox"
        checked={editingProduct.data.is_trend === true}
        onChange={(e) =>
          updateEditingProductSingle("is_trend", e.target.checked)
        }
        className="accent-orange-600 w-5 h-5"
      />
    </label>
  </div>
</div>       <textarea
  placeholder={`Kısa menü açıklaması (${formLang.toUpperCase()})`}

  className="w-full p-3 border rounded-xl mt-3"
  rows={4}
  value={editingProduct.data.description?.[formLang] || ""}
  onChange={(e) =>
    updateEditingProduct("description", formLang, e.target.value)
  }
/>

<div className="border border-emerald-100 bg-emerald-50 rounded-2xl p-4 mt-4">
  <div className="flex items-start justify-between gap-3 mb-3">
    <div>
      <div className="font-extrabold text-gray-900">
        ✅ Mevzuata Uyumlu Menü Bilgileri
      </div>

      <p className="text-xs text-gray-500 mt-1">
        İçindekiler, kalori, porsiyon ve özel uyarılar QR menüde gösterilir.
      </p>
    </div>
    
  </div>

  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
    <div>
      <label className="block text-xs font-extrabold text-gray-600 mb-1">
        Kalori
      </label>

<div className="flex flex-col sm:flex-row gap-2">
  <input
    type="number"
    inputMode="numeric"
    placeholder="Örn: 740"
    className="w-full p-3 border rounded-xl bg-white font-bold"
    value={
      normalizeComplianceInfo(editingProduct.data.compliance_info)
        .calories || ""
    }
    onChange={(e) =>
      updateEditingComplianceSingle("calories", e.target.value)
    }
  />

  <button
    type="button"
    onClick={applyEditingCalorieEstimate}
    className="shrink-0 px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-xs font-black shadow hover:shadow-lg active:scale-95"
  >
    ✨ Tahmini Oluştur
  </button>
</div>

<p className="text-[11px] text-gray-500 mt-1">
  Manuel girebilir veya sistemin önerdiği tahmini kaloriyi kullanabilirsiniz.
</p>

{normalizeComplianceInfo(editingProduct.data.compliance_info)
  .calories_estimated && (
  <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-xl p-2 mt-2">
    {normalizeComplianceInfo(editingProduct.data.compliance_info)
      .calorie_note?.[formLang] || CALORIE_ESTIMATE_NOTE.tr}
  </p>
)}
    </div>

    <div>
      <label className="block text-xs font-extrabold text-gray-600 mb-1">
        Porsiyon / Gramaj ({formLang.toUpperCase()})
      </label>

      <input
        placeholder="Örn: 1 porsiyon / 250 gr / 300 ml"
        className="w-full p-3 border rounded-xl bg-white font-bold"
        value={
          normalizeComplianceInfo(editingProduct.data.compliance_info)
            .portion?.[formLang] || ""
        }
        onChange={(e) =>
          updateEditingComplianceLang("portion", formLang, e.target.value)
        }
      />
    </div>
  </div>

  <div className="mt-3">
    <label className="block text-xs font-extrabold text-gray-600 mb-1">
      İçindekiler / Bileşenler ({formLang.toUpperCase()})
    </label>

    <textarea
      placeholder="Örn: Dana eti, lavaş, domates, soğan, yoğurt sos"
      className="w-full p-3 border rounded-xl bg-white"
      rows={3}
      value={
        normalizeComplianceInfo(editingProduct.data.compliance_info)
          .ingredients?.[formLang] || ""
      }
      onChange={(e) =>
        updateEditingComplianceLang("ingredients", formLang, e.target.value)
      }
    />
  </div>

  <div className="mt-3">
    <label className="block text-xs font-extrabold text-gray-600 mb-1">
      Özel Uyarı ({formLang.toUpperCase()})
    </label>

    <textarea
placeholder="Örn: Acı içerir, sirke/sarımsak ile servis edilir, süt ürünü içerir."      className="w-full p-3 border rounded-xl bg-white"
      rows={2}
      value={
        normalizeComplianceInfo(editingProduct.data.compliance_info)
          .warning?.[formLang] || ""
      }
      onChange={(e) =>
        updateEditingComplianceLang("warning", formLang, e.target.value)
      }
    />
  </div>

  <div className="mt-4">
    <div className="text-xs font-black text-gray-600 mb-2">
      Beslenme / İçerik Etiketleri
    </div>

    <div className="flex flex-wrap gap-2">
      {DIETARY_TAGS.map((tag) => {
        const tags =
          normalizeComplianceInfo(editingProduct.data.compliance_info).tags ||
          [];
        const active = tags.includes(tag.key);

        return (
          <button
            key={tag.key}
            type="button"
            onClick={() => toggleEditingComplianceTag(tag.key)}
            className={`px-3 py-2 rounded-full text-xs border font-bold ${
              active
                ? "bg-emerald-600 text-white border-emerald-600"
                : "bg-white text-gray-700 border-gray-200"
            }`
}
          >
            {tag.label}
          </button>
        );
      })}
    </div>
  </div>
</div>

<div className="flex flex-wrap gap-2 mt-4">
  {ALLERGEN_KEYS.map((key) => {
            const list = editingProduct.data.allergens || [];
            const active = list.includes(key);

            return (
              <button
                key={key}
                type="button"
                className={`px-3 py-1 rounded-full text-sm border ${
                  active
                    ? "bg-orange-600 text-white"
                    : "bg-white text-gray-700"
                }`}
                onClick={() =>
                  updateEditingProductSingle(
                    "allergens",
                    active
                      ? list.filter((x) => x !== key)
                      : [...list, key]
                  )
                }
              >
                {ALLERGEN_LABELS[key]}
              </button>
            );
          })}
        </div>

        <div className="flex flex-col md:flex-row gap-3 mt-6">
          <button
            type="button"
            onClick={saveEditingProduct}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl font-bold"
          >
            Kaydet
          </button>

          <button
            type="button"
            onClick={() => setEditingProduct(null)}
            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 py-3 rounded-xl font-bold"
          >
            Vazgeç
          </button>
        </div>
      </div>
    </div>
  </div>
)}


<div className="bg-white/95 backdrop-blur rounded-3xl shadow-2xl border border-white/70 p-5 md:p-6 mb-6">

  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
    <div>
      <h2 className="text-xl font-extrabold text-gray-900">
        ⚙️ QR Menü Özellikleri
      </h2>
      <p className="text-sm text-gray-500 mt-1">
        Bu işletme için garson çağırma ve sipariş alma özelliklerini açıp kapatın.
      </p>
    </div>

    <button
      type="button"
      onClick={saveMenuOptions}
      className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-bold"
    >
      Kaydet
    </button>
  </div>

  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
    <label className="flex items-center justify-between border rounded-xl p-4 cursor-pointer bg-gray-50">
      <div>
        <div className="font-bold text-gray-900">🔔 Garson Çağır</div>
        <div className="text-sm text-gray-500">Müşteri masadan garson çağırabilir.</div>
      </div>

      <input
        type="checkbox"
        checked={menuOptions.garson_cagir_active}
        onChange={(e) =>
          setMenuOptions({
            ...menuOptions,
            garson_cagir_active: e.target.checked,
          })
        }
        className="w-5 h-5 accent-orange-600"
      />
    </label>

    <label className="flex items-center justify-between border rounded-xl p-4 cursor-pointer bg-gray-50">
      <div>
        <div className="font-bold text-gray-900">🛒 Sipariş Ver</div>
        <div className="text-sm text-gray-500">Müşteri sepetten sipariş gönderebilir.</div>
      </div>

      <input
        type="checkbox"
        checked={menuOptions.siparis_active}
        onChange={(e) =>
          setMenuOptions({
            ...menuOptions,
            siparis_active: e.target.checked,
          })
        }
        className="w-5 h-5 accent-orange-600"
      />
    </label>
  </div>



{/* 🎨 TEMA TERCİHLERİ */}
<details className="mt-8 border-t pt-6 group">
  <summary className="list-none cursor-pointer rounded-2xl border bg-gradient-to-r from-orange-50 to-white p-4 hover:shadow transition">
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
      <div>
        <h3 className="text-lg font-extrabold text-gray-900">
          🎨 Tema Tercihleri
        </h3>

        <p className="text-xs text-gray-500 mt-1">
          Temaları açıp kapatın...
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-extrabold bg-orange-100 text-orange-700 border border-orange-200 px-3 py-2 rounded-full">
          {(menuOptions.theme_options || []).length} tema seçili
        </span>

        <span className="text-xs font-bold bg-white text-gray-600 border px-3 py-2 rounded-full">
          Aç / Kapat
        </span>
      </div>
    </div>
  </summary>

  <div className="mt-4 rounded-2xl border bg-gray-50 p-3">
    <div className="max-h-[360px] overflow-y-auto pr-1">
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 xl:grid-cols-8 gap-2">
        {QR_THEMES.map((theme) => {
          const selectedThemes = menuOptions.theme_options || [];
          const active = selectedThemes.includes(theme.id);
          const isDefault = menuOptions.theme_default === theme.id;

          return (
            <div
              key={theme.id}
              role="button"
              tabIndex={0}
              onClick={() => {
                let list = menuOptions.theme_options || [];

                if (list.includes(theme.id)) {
                  if (list.length === 1) {
                    showAppNotice("themeRequired", "error");
                    return;
                  }

                  list = list.filter((x) => x !== theme.id);
                } else {
                  list = [...list, theme.id];
                }

                let defaultTheme = menuOptions.theme_default || "standard";

                if (!list.includes(defaultTheme)) {
                  defaultTheme = list[0] || "standard";
                }

                setMenuOptions({
                  ...menuOptions,
                  theme_options: list,
                  theme_default: defaultTheme,
                });
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.currentTarget.click();
                }
              }}
              className={`relative min-h-[78px] rounded-xl border cursor-pointer text-center transition p-2 flex flex-col items-center justify-center ${
                active
                  ? "bg-orange-600 text-white border-orange-600 shadow-md"
                  : "bg-white text-gray-700 border-gray-200 hover:border-orange-300 hover:bg-orange-50"
              }`}
            >
              {active && (
                <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-white text-orange-600 text-xs font-extrabold flex items-center justify-center">
                  ✓
                </div>
              )}

              <div className="text-2xl leading-none">
                {theme.icon}
              </div>

              <div
                className="font-extrabold text-[11px] mt-1 leading-tight max-w-full"
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {theme.name}
              </div>

              {isDefault && (
                <div className="text-[10px] font-extrabold mt-1 bg-white/20 px-2 py-0.5 rounded-full">
                  Vars.
                </div>
              )}

              {active && !isDefault && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();

                    setMenuOptions({
                      ...menuOptions,
                      theme_default: theme.id,
                    });
                  }}
                  className={`mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    active
                      ? "bg-white text-orange-700"
                      : "bg-orange-100 text-orange-700"
                  }`}
                >
                  Varsayılan Yap
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>

    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mt-4">
      <div className="text-xs text-gray-500">
        Not: En az 1 tema seçili kalmalıdır. Varsayılan tema müşteri menüsünde ilk tema olarak kullanılır.
      </div>

      <button
        type="button"
        onClick={saveMenuOptions}
        className="bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-xl font-extrabold text-sm"
      >
        🎨 Tema Ayarlarını Kaydet
      </button>
    </div>
  </div>
</details>
 


          <div className="bg-gradient-to-br from-slate-950 via-slate-900 to-orange-900 rounded-3xl shadow-2xl border border-white/10 p-5 md:p-6 mt-8 text-white">
        <h2 className="text-xl font-extrabold mb-4 text-white">
          ⭐ Google Yorum Ayarı
        </h2>

        <input
          className="w-full border border-white/20 rounded-xl p-3 mb-4 text-gray-900"          placeholder="Google yorum linki"
          value={googleReview.google_review_url}
          onChange={(e) =>
            setGoogleReview({
              ...googleReview,
              google_review_url: e.target.value,
            })
          }
        />

        <div className="flex flex-wrap gap-2 mb-4">
          {menuLangs.map((lng) => (
            <button
              key={lng}
              type="button"
              onClick={() => setFormLang(lng)}
              className={`px-4 py-2 rounded-full font-bold border ${
                formLang === lng ? "bg-orange-600 text-white" : "bg-white"
              }`}
            >
              {lng.toUpperCase()}
            </button>
          ))}
        </div>

        <input
          className="w-full border border-white/20 rounded-xl p-3 mb-3 text-gray-900"          placeholder="Başlık"
          value={googleReview.google_review_title_json[formLang] || ""}
          onChange={(e) =>
            setGoogleReview({
              ...googleReview,
              google_review_title_json: {
                ...googleReview.google_review_title_json,
                [formLang]: e.target.value,
              },
            })
          }
        />

        <textarea
          className="w-full border border-white/20 rounded-xl p-3 text-gray-900"          rows="3"
          placeholder="Açıklama"
          value={googleReview.google_review_desc_json[formLang] || ""}
          onChange={(e) =>
            setGoogleReview({
              ...googleReview,
              google_review_desc_json: {
                ...googleReview.google_review_desc_json,
                [formLang]: e.target.value,
              },
            })
          }
        />

        <label className="flex gap-2 mt-4 font-bold">
          <input
            type="checkbox"
            checked={googleReview.google_review_active}
            onChange={(e) =>
              setGoogleReview({
                ...googleReview,
                google_review_active: e.target.checked,
              })
            }
          />
          Google yorum butonunu göster
        </label>

                <button
          type="button"
          onClick={saveGoogleReview}
          className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 py-3 rounded-2xl font-extrabold mt-5 shadow-xl active:scale-95 transition"        >
          Kaydet
        </button>
      </div>
    </div>
    </div>
  );
}





